<template>
	<div>
		<!-- Header -->
		<div class="page-header d-flex align-items-center my-4">
			<router-link to="/auto-campaigns" class="back-btn me-3" title="Back to Auto Campaigns">
				<i class="fad fa-arrow-left"></i>
			</router-link>
			<div>
				<h2 class="page-title">Auto Campaign Settings</h2>
				<p class="page-subtitle">Manage your auto campaign configuration here</p>
			</div>
		</div>

		<!-- Tab Navigation -->
		<ul class="nav nav-pills custom-nav-pills mb-4" role="tablist">
			<li class="nav-item" role="presentation">
				<button class="nav-link" :class="{ active: activeTab === 'main' }" @click="setActiveTab('main')" type="button" role="tab">
					<span class="tab-text">Main</span>
				</button>
			</li>
			<li class="nav-item" role="presentation">
				<button class="nav-link" :class="{ active: activeTab === 'triggers' }" @click="setActiveTab('triggers')" type="button" role="tab">
					<span class="tab-text">Triggers</span>
					<span class="tab-badge">{{ triggers ? triggers.length : 0 }}</span>
				</button>
			</li>
			<li class="nav-item" role="presentation">
				<button class="nav-link" :class="{ active: activeTab === 'senders' }" @click="setActiveTab('senders')" type="button" role="tab">
					<span class="tab-text">Senders</span>
					<span class="tab-badge">{{ senders ? senders.length : 0 }}</span>
				</button>
			</li>
			<li class="nav-item" role="presentation">
				<button class="nav-link" :class="{ active: activeTab === 'receivers' }" @click="setActiveTab('receivers')" type="button" role="tab">
					<span class="tab-text">Receivers</span>
					<span class="tab-badge">{{ receivers ? receivers.length : 0 }}</span>
				</button>
			</li>
			<li class="nav-item" role="presentation">
				<button class="nav-link" :class="{ active: activeTab === 'emailProviders' }" @click="setActiveTab('emailProviders')" type="button" role="tab">
					<span class="tab-text">Email Providers</span>
					<span class="tab-badge">{{ emailProviderConfigs ? emailProviderConfigs.length : 0 }}</span>
				</button>
			</li>
			<li class="nav-item" role="presentation">
				<button class="nav-link" :class="{ active: activeTab === 'emailTemplates' }" @click="setActiveTab('emailTemplates')" type="button" role="tab">
					<span class="tab-text">Email Templates</span>
					<span class="tab-badge">{{ emailTemplates ? emailTemplates.length : 0 }}</span>
				</button>
			</li>
			<li class="nav-item" role="presentation">
				<button class="nav-link" :class="{ active: activeTab === 'emailQueue' }" @click="setActiveTab('emailQueue')" type="button" role="tab">
					<span class="tab-text">Email Queue</span>
					<span class="tab-badge">{{ emailQueueMeta.total || 0 }}</span>
				</button>
			</li>
			<li class="nav-item" role="presentation">
				<button class="nav-link" :class="{ active: activeTab === 'cron' }" @click="setActiveTab('cron')" type="button" role="tab">
					<span class="tab-text">Cron</span>
				</button>
			</li>
			<li class="nav-item" role="presentation">
				<button class="nav-link" :class="{ active: activeTab === 'notifications' }" @click="setActiveTab('notifications')" type="button" role="tab">
					<span class="tab-text">Notifications</span>
				</button>
			</li>
			<li class="nav-item" role="presentation">
				<button class="nav-link" :class="{ active: activeTab === 'statistics' }" @click="setActiveTab('statistics')" type="button" role="tab">
					<span class="tab-text">Statistics</span>
					<span class="tab-badge">📊</span>
				</button>
			</li>
		</ul>

		<!-- Tab Content -->
		<div class="tab-content">
			<!-- Main Tab -->
			<div v-if="activeTab === 'main'" class="tab-pane active">
				<div class="row">
					<div class="col-md-6">
						<div class="settings-section">
							<div class="section-header-main">
								<h4 class="section-title-main">
									<img
										src="https://global-uploads.webflow.com/5e1e5c62fa3d4447af417098/634878fe6d056d6eb407be82_Lob%20Favicon.png"
										width="24"
										alt=""
										class="me-2"
									/>
									LOB Settings
								</h4>
								<p class="section-description-main">Configure LOB API settings for campaigns</p>
							</div>
							<div class="section-content-main">
								<form v-if="lobSettings" @submit.prevent="saveLobSettings">
									<div class="form-group mb-3">
										<label class="form-label">LOB API URL</label>
										<input type="text" class="form-control" v-model="lobSettings.apiUrl" />
									</div>
									<div class="form-group mb-3">
										<label class="form-label">LOB API Version</label>
										<input type="text" class="form-control" v-model="lobSettings.apiVersion" />
									</div>
									<div class="form-group mb-3">
										<label class="form-label">LOB API Key (Production)</label>
										<input type="text" class="form-control" v-model="lobSettings.apiKey" />
									</div>
									<div class="form-group mb-3">
										<label class="form-label">LOB API Key (Test)</label>
										<input type="text" class="form-control" v-model="lobSettings.apiKeyTest" />
									</div>

									<div class="d-flex justify-content-end mt-4">
										<button type="submit" class="save-btn" :disabled="savingLobSettings">
											<i :class="['fad', savingLobSettings ? 'fa-spinner fa-spin' : 'fa-save', 'save-icon me-2']"></i>
											Save
										</button>
									</div>
								</form>

								<div v-else class="text-center p-4">
									<div class="spinner-border text-primary" role="status">
										<span class="visually-hidden">Loading settings...</span>
									</div>
									<p class="mt-2 text-muted">Loading LOB settings...</p>
								</div>
							</div>
						</div>
					</div>
					<div class="col-md-6">
						<!-- Second half available for future content -->
					</div>
				</div>
			</div>

			<!-- Triggers Tab -->
			<div v-if="activeTab === 'triggers'" class="tab-pane active">
				<div class="settings-section">
					<div class="d-flex justify-content-between align-items-center p-4">
						<h3 class="section-title">Triggers Management</h3>
						<button
							class="add-trigger-btn"
							@click="openCreateTriggerOffcanvas"
							data-bs-toggle="offcanvas"
							data-bs-target="#createTriggerOffcanvas"
							title="Create Trigger"
						>
							<i class="fad fa-plus btn-icon"></i>
							<span class="btn-tooltip">Create Trigger</span>
						</button>
					</div>

					<!-- Loading State -->
					<div v-if="loadingTriggers" class="text-center p-4">
						<div class="spinner-border text-primary" role="status">
							<span class="visually-hidden">Loading triggers...</span>
						</div>
						<p class="mt-2 text-muted">Loading triggers...</p>
					</div>

					<!-- Triggers Table -->
					<div v-else-if="triggers.length > 0" class="triggers-table">
						<!-- Table Header -->
						<div class="table-header">
							<div class="header-cell trigger-col">Trigger</div>
							<div class="header-cell type-col">Type</div>
							<div class="header-cell schedule-col">Schedule</div>
							<div class="header-cell url-col">Hook URL</div>
							<div class="header-cell actions-col"></div>
						</div>

						<!-- Table Rows -->
						<div class="table-row" v-for="trigger in triggers" :key="trigger.id">
							<div class="table-cell trigger-cell">
								<div class="trigger-info">
									<div class="trigger-avatar">
										{{ getInitials(trigger.name) }}
									</div>
									<div class="trigger-details">
										<div class="trigger-name">{{ trigger.name }}</div>
										<div class="trigger-description">{{ trigger.reason || 'No description' }}</div>
									</div>
								</div>
							</div>
							<div class="table-cell type-cell">
								<span v-if="trigger.trigger_date && !trigger.recurring_type" class="type-badge date">Date</span>
								<span v-if="trigger.recurring_type" class="type-badge recurring">{{ trigger.recurring_type | capitalize }}</span>
								<span v-if="trigger.hook" class="type-badge hook">Hook</span>
								<span v-else-if="!trigger.trigger_date && !trigger.recurring_type" class="type-badge date">Manual</span>
							</div>
							<div class="table-cell schedule-cell">
								<div class="schedule-info">
									<div v-if="trigger.trigger_date && !trigger.recurring_type" class="schedule-type">{{ formatTriggerDate(trigger.trigger_date) }}</div>
									<div v-else-if="trigger.recurring_type === 'daily'" class="schedule-type">Daily at {{ trigger.recurring_time || 'N/A' }}</div>
									<div v-else-if="trigger.recurring_type === 'weekly'" class="schedule-type">
										{{ getDayName(trigger.recurring_day) }} at {{ trigger.recurring_time || 'N/A' }}
									</div>
									<div v-else-if="trigger.recurring_type === 'yearly'" class="schedule-type">
										{{ trigger.recurring_date }} at {{ trigger.recurring_time || 'N/A' }}
									</div>
									<div v-else class="schedule-type">N/A</div>
								</div>
							</div>
							<div class="table-cell url-cell">
								<span v-if="trigger.hook" class="hook-url" :title="trigger.hook">{{ trigger.hook }}</span>
								<span v-else class="no-url">N/A</span>
							</div>
							<div class="table-cell actions-cell">
								<button
									type="button"
									class="action-icon-btn"
									@click="editTrigger(trigger)"
									data-bs-toggle="offcanvas"
									data-bs-target="#createTriggerOffcanvas"
									title="Edit"
								>
									<i class="fad fa-pen"></i>
								</button>
								<button type="button" class="action-icon-btn delete-btn" @click="deleteTrigger(trigger.id)" title="Delete">
									<i class="fad fa-trash"></i>
								</button>
							</div>
						</div>
					</div>

					<div v-else-if="!loadingTriggers" class="empty-state">
						<h3 class="empty-title">No Triggers Found</h3>
						<p class="empty-description">Create your first trigger to get started with automated campaigns.</p>
						<button class="empty-state-btn" @click="openCreateTriggerOffcanvas" data-bs-toggle="offcanvas" data-bs-target="#createTriggerOffcanvas">
							<i class="fad fa-plus btn-icon me-2"></i>
							Create First Trigger
						</button>
					</div>
				</div>
			</div>

			<!-- Senders Tab -->
			<div v-if="activeTab === 'senders'" class="tab-pane active">
				<div class="settings-section">
					<div class="d-flex justify-content-between align-items-center p-4">
						<h3 class="section-title">Campaign Senders</h3>
						<button class="add-trigger-btn" @click="openCreateSenderOffcanvas" data-bs-toggle="offcanvas" data-bs-target="#createSenderOffcanvas" title="Add Sender">
							<i class="fad fa-plus btn-icon"></i>
							<span class="btn-tooltip">Add Sender</span>
						</button>
					</div>

					<!-- Loading State -->
					<div v-if="loadingSenders" class="text-center p-4">
						<div class="spinner-border text-primary" role="status">
							<span class="visually-hidden">Loading senders...</span>
						</div>
						<p class="mt-2 text-muted">Loading senders...</p>
					</div>

					<!-- Senders Table -->
					<div v-else-if="senders.length > 0" class="triggers-table senders">
						<!-- Table Header -->
						<div class="table-header">
							<div class="header-cell sender-col">Sender</div>
							<div class="header-cell address-col">Address</div>
							<div class="header-cell city-col">City</div>
							<div class="header-cell state-col">State</div>
							<div class="header-cell zip-col">ZIP</div>
							<div class="header-cell actions-col"></div>
						</div>

						<!-- Table Rows -->
						<div class="table-row" v-for="(sender, index) in senders" :key="index">
							<div class="table-cell sender-cell">
								<div class="trigger-info">
									<div class="trigger-avatar">
										{{ getInitials(sender.name) }}
									</div>
									<div class="trigger-details">
										<div class="trigger-name">{{ sender.name || 'Unnamed Sender' }}</div>
										<div class="trigger-description">Campaign Sender</div>
									</div>
								</div>
							</div>
							<div class="table-cell address-cell">{{ sender.address_line1 || 'N/A' }}</div>
							<div class="table-cell city-cell">{{ sender.address_city || 'N/A' }}</div>
							<div class="table-cell state-cell">{{ sender.address_state || 'N/A' }}</div>
							<div class="table-cell zip-cell">{{ sender.address_zip || 'N/A' }}</div>
							<div class="table-cell actions-cell">
								<button
									type="button"
									class="action-icon-btn"
									@click="editSender(sender, index)"
									data-bs-toggle="offcanvas"
									data-bs-target="#createSenderOffcanvas"
									title="Edit"
								>
									<i class="fad fa-pen"></i>
								</button>
								<button type="button" class="action-icon-btn delete-btn" @click="deleteSender(index)" title="Delete">
									<i class="fad fa-trash"></i>
								</button>
							</div>
						</div>
					</div>

					<div v-else-if="!loadingSenders" class="empty-state">
						<h3 class="empty-title">No Senders Found</h3>
						<p class="empty-description">Add your first campaign sender to get started.</p>
						<button class="empty-state-btn" @click="openCreateSenderOffcanvas" data-bs-toggle="offcanvas" data-bs-target="#createSenderOffcanvas">
							<i class="fad fa-plus btn-icon me-2"></i>
							Add First Sender
						</button>
					</div>
				</div>
			</div>

			<!-- Receivers Tab -->
			<div v-if="activeTab === 'receivers'" class="tab-pane active">
				<div class="settings-section">
					<div class="d-flex justify-content-between align-items-start p-4">
						<div class="me-4">
							<h3 class="section-title">Always-On Receivers</h3>
							<p class="section-description mb-0">
								Every enabled receiver gets a copy of <strong>every</strong> campaign: each auto campaign run, each mailed sequence of a
								scheduled campaign, and every campaign created under Campaigns. Free-form addresses only, and mail only — receivers
								never enter the email queue. They are counted in the campaign totals and cost.
							</p>
						</div>
						<button
							class="add-trigger-btn"
							@click="openCreateReceiverOffcanvas"
							data-bs-toggle="offcanvas"
							data-bs-target="#createReceiverOffcanvas"
							title="Add Receiver"
						>
							<i class="fad fa-plus btn-icon"></i>
							<span class="btn-tooltip">Add Receiver</span>
						</button>
					</div>

					<!-- Receivers Table -->
					<div v-if="receivers.length > 0" class="triggers-table receivers">
						<!-- Table Header -->
						<div class="table-header">
							<div class="header-cell receiver-col">Receiver</div>
							<div class="header-cell address-col">Address</div>
							<div class="header-cell city-col">City</div>
							<div class="header-cell state-col">State</div>
							<div class="header-cell zip-col">ZIP</div>
							<div class="header-cell status-col">Status</div>
							<div class="header-cell actions-col"></div>
						</div>

						<!-- Table Rows -->
						<div class="table-row" v-for="(receiver, index) in receivers" :key="index">
							<div class="table-cell receiver-cell">
								<div class="trigger-info">
									<div class="trigger-avatar">
										{{ getInitials(receiver.name) }}
									</div>
									<div class="trigger-details">
										<div class="trigger-name">{{ receiver.name || 'Unnamed Receiver' }}</div>
										<div class="trigger-description">{{ receiverScheduleLabel(receiver) }}</div>
									</div>
								</div>
							</div>
							<div class="table-cell address-cell">{{ receiver.address || 'N/A' }}</div>
							<div class="table-cell city-cell">{{ receiver.city || 'N/A' }}</div>
							<div class="table-cell state-cell">{{ receiver.state || 'N/A' }}</div>
							<div class="table-cell zip-cell">{{ receiver.zip || 'N/A' }}</div>
							<div class="table-cell status-cell">
								<button
									type="button"
									class="status-toggle-btn"
									@click="toggleReceiver(index)"
									:title="receiver.enabled === false ? 'Enable receiver' : 'Disable receiver'"
								>
									<span class="type-badge" :class="receiver.enabled === false ? 'date' : 'recurring'">
										{{ receiver.enabled === false ? 'disabled' : 'enabled' }}
									</span>
								</button>
							</div>
							<div class="table-cell actions-cell">
								<button
									type="button"
									class="action-icon-btn"
									@click="editReceiver(receiver, index)"
									data-bs-toggle="offcanvas"
									data-bs-target="#createReceiverOffcanvas"
									title="Edit"
								>
									<i class="fad fa-pen"></i>
								</button>
								<button type="button" class="action-icon-btn delete-btn" @click="deleteReceiver(index)" title="Delete">
									<i class="fad fa-trash"></i>
								</button>
							</div>
						</div>
					</div>

					<div v-else class="empty-state">
						<h3 class="empty-title">No Receivers Found</h3>
						<p class="empty-description">Add an address here and it will be mailed a copy of every campaign.</p>
						<button class="empty-state-btn" @click="openCreateReceiverOffcanvas" data-bs-toggle="offcanvas" data-bs-target="#createReceiverOffcanvas">
							<i class="fad fa-plus btn-icon me-2"></i>
							Add First Receiver
						</button>
					</div>
				</div>
			</div>

			<div v-if="activeTab === 'emailProviders'" class="tab-pane active">
				<div class="settings-section">
					<div class="d-flex justify-content-between align-items-center p-4">
						<h3 class="section-title">Email Provider Configs</h3>
						<button class="add-trigger-btn" @click="openCreateEmailProviderOffcanvas" data-bs-toggle="offcanvas" data-bs-target="#createEmailProviderOffcanvas" title="Add Email Provider">
							<i class="fad fa-plus btn-icon"></i>
							<span class="btn-tooltip">Add Provider</span>
						</button>
					</div>

					<div v-if="loadingEmailProviders" class="text-center p-4">
						<div class="spinner-border text-primary" role="status">
							<span class="visually-hidden">Loading providers...</span>
						</div>
						<p class="mt-2 text-muted">Loading email providers...</p>
					</div>

					<div v-else-if="emailProviderConfigs.length > 0" class="triggers-table senders">
						<div class="table-header">
							<div class="header-cell sender-col">Name</div>
							<div class="header-cell type-col">Provider</div>
							<div class="header-cell city-col">From Email</div>
							<div class="header-cell state-col">Status</div>
							<div class="header-cell actions-col"></div>
						</div>

						<div class="table-row" v-for="provider in emailProviderConfigs" :key="provider.id">
							<div class="table-cell sender-cell">
								<div class="trigger-info">
									<div class="trigger-avatar">
										{{ getInitials(provider.name) }}
									</div>
									<div class="trigger-details">
										<div class="trigger-name">{{ provider.name }}</div>
										<div class="trigger-description">{{ provider.default_from_name || 'No default sender name' }}</div>
									</div>
								</div>
							</div>
							<div class="table-cell type-cell">
								<span class="type-badge hook">{{ provider.provider }}</span>
							</div>
							<div class="table-cell city-cell">{{ provider.default_from_email || 'N/A' }}</div>
							<div class="table-cell state-cell">
								<span class="type-badge" :class="provider.status === 'active' ? 'recurring' : 'date'">{{ provider.status }}</span>
							</div>
							<div class="table-cell actions-cell">
								<button type="button" class="action-icon-btn" @click="editEmailProvider(provider)" data-bs-toggle="offcanvas" data-bs-target="#createEmailProviderOffcanvas" title="Edit">
									<i class="fad fa-pen"></i>
								</button>
								<button type="button" class="action-icon-btn delete-btn" @click="deleteEmailProvider(provider.id)" title="Delete">
									<i class="fad fa-trash"></i>
								</button>
							</div>
						</div>
					</div>

					<div v-else-if="!loadingEmailProviders" class="empty-state">
						<h3 class="empty-title">No Email Providers</h3>
						<p class="empty-description">Create your first email provider config for SendGrid, Postmark or Instantly.</p>
						<button class="empty-state-btn" @click="openCreateEmailProviderOffcanvas" data-bs-toggle="offcanvas" data-bs-target="#createEmailProviderOffcanvas">
							<i class="fad fa-plus btn-icon me-2"></i>
							Add First Provider
						</button>
					</div>
				</div>
			</div>

			<div v-if="activeTab === 'emailTemplates'" class="tab-pane active">
				<div class="settings-section">
					<div class="d-flex justify-content-between align-items-center p-4">
						<h3 class="section-title">Email Templates</h3>
						<button class="add-trigger-btn" @click="openCreateEmailTemplateOffcanvas" data-bs-toggle="offcanvas" data-bs-target="#createEmailTemplateOffcanvas" title="Add Email Template">
							<i class="fad fa-plus btn-icon"></i>
							<span class="btn-tooltip">Add Template</span>
						</button>
					</div>

					<div v-if="loadingEmailTemplates" class="text-center p-4">
						<div class="spinner-border text-primary" role="status">
							<span class="visually-hidden">Loading templates...</span>
						</div>
						<p class="mt-2 text-muted">Loading email templates...</p>
					</div>

					<div v-else-if="emailTemplates.length > 0" class="triggers-table">
						<div class="table-header">
							<div class="header-cell trigger-col">Template</div>
							<div class="header-cell type-col">Code</div>
							<div class="header-cell schedule-col">Subject</div>
							<div class="header-cell url-col">Status</div>
							<div class="header-cell actions-col"></div>
						</div>

						<div class="table-row" v-for="template in emailTemplates" :key="template.id">
							<div class="table-cell trigger-cell">
								<div class="trigger-info">
									<div class="trigger-avatar">{{ getInitials(template.name) }}</div>
									<div class="trigger-details">
										<div class="trigger-name">{{ template.name }}</div>
										<div class="trigger-description">{{ template.description || 'No description' }}</div>
									</div>
								</div>
							</div>
							<div class="table-cell type-cell">
								<span class="hook-url">{{ template.code }}</span>
							</div>
							<div class="table-cell schedule-cell">
								<div class="schedule-type">{{ template.subject }}</div>
							</div>
							<div class="table-cell url-cell">
								<span class="type-badge" :class="template.status === 'active' ? 'recurring' : template.status === 'draft' ? 'date' : 'hook'">{{ template.status }}</span>
							</div>
							<div class="table-cell actions-cell">
								<button type="button" class="action-icon-btn" @click="editEmailTemplate(template)" data-bs-toggle="offcanvas" data-bs-target="#createEmailTemplateOffcanvas" title="Edit">
									<i class="fad fa-pen"></i>
								</button>
								<button type="button" class="action-icon-btn delete-btn" @click="deleteEmailTemplate(template.id)" title="Delete">
									<i class="fad fa-trash"></i>
								</button>
							</div>
						</div>
					</div>

					<div v-else-if="!loadingEmailTemplates" class="empty-state">
						<h3 class="empty-title">No Email Templates</h3>
						<p class="empty-description">Create reusable email templates for campaigns and sequence steps.</p>
						<button class="empty-state-btn" @click="openCreateEmailTemplateOffcanvas" data-bs-toggle="offcanvas" data-bs-target="#createEmailTemplateOffcanvas">
							<i class="fad fa-plus btn-icon me-2"></i>
							Add First Template
						</button>
					</div>
				</div>
			</div>

			<div v-if="activeTab === 'emailQueue'" class="tab-pane active">
				<div class="settings-section">
					<div class="d-flex justify-content-between align-items-center p-4">
						<h3 class="section-title">Campaign Email Queue</h3>
						<button class="btn btn-outline-primary btn-sm" @click="processScheduledEmails" :disabled="processingScheduledEmails">
							<i :class="['fad', processingScheduledEmails ? 'fa-spinner fa-spin' : 'fa-paper-plane', 'me-2']"></i>
							Process Scheduled
						</button>
					</div>

					<div class="p-4 border-bottom d-flex gap-3 align-items-end flex-wrap">
						<div class="form-group mb-0">
							<label class="form-label">Campaign Source</label>
							<select class="form-select" v-model="emailQueueFilters.campaign_source" @change="fetchEmailQueue">
								<option value="">All</option>
								<option value="auto_campaign">Auto Campaign</option>
								<option value="sequenced_campaign">Sequenced Campaign</option>
							</select>
						</div>
						<div class="form-group mb-0">
							<label class="form-label">Status</label>
							<select class="form-select" v-model="emailQueueFilters.status" @change="fetchEmailQueue">
								<option value="">All</option>
								<option value="prepared">Prepared</option>
								<option value="scheduled">Scheduled</option>
								<option value="queued">Queued</option>
								<option value="sent">Sent</option>
								<option value="delivered">Delivered</option>
								<option value="opened">Opened</option>
								<option value="clicked">Clicked</option>
								<option value="bounced">Bounced</option>
								<option value="failed">Failed</option>
							</select>
						</div>
						<button class="btn btn-outline-secondary btn-sm mb-0" @click="clearEmailQueueFilters">Clear</button>
					</div>

					<div v-if="loadingEmailQueue" class="text-center p-4">
						<div class="spinner-border text-primary" role="status">
							<span class="visually-hidden">Loading queue...</span>
						</div>
						<p class="mt-2 text-muted">Loading campaign email queue...</p>
					</div>

					<div v-else-if="emailQueueItems.length > 0" class="triggers-table">
						<div class="table-header">
							<div class="header-cell trigger-col">Recipient</div>
							<div class="header-cell type-col">Source</div>
							<div class="header-cell schedule-col">Template</div>
							<div class="header-cell url-col">Status</div>
							<div class="header-cell schedule-col">Retry</div>
							<div class="header-cell actions-col">Scheduled</div>
						</div>

						<div class="table-row" v-for="item in emailQueueItems" :key="item.id">
							<div class="table-cell trigger-cell">
								<div class="trigger-info">
									<div class="trigger-avatar">{{ getInitials(item.recipient_name || item.recipient_email) }}</div>
									<div class="trigger-details">
										<div class="trigger-name">{{ item.recipient_name || 'No name' }}</div>
										<div class="trigger-description">{{ item.recipient_email }}</div>
									</div>
								</div>
							</div>
							<div class="table-cell type-cell">
								<span class="type-badge hook">{{ item.campaign_source }}</span>
							</div>
							<div class="table-cell schedule-cell">
								<div class="schedule-type">{{ (item.email_template && item.email_template.name) || 'Snapshot' }}</div>
							</div>
							<div class="table-cell url-cell">
								<span class="type-badge" :class="getEmailQueueStatusClass(item.status)">{{ item.status }}</span>
							</div>
							<div class="table-cell schedule-cell">
								<div class="schedule-type">{{ item.attempt_count || 0 }} / {{ item.max_attempts || 5 }}</div>
								<div class="trigger-description">{{ formatQueueDate(item.next_attempt_at) }}</div>
							</div>
							<div class="table-cell actions-cell">
								<span class="hook-url">{{ formatQueueDate(item.scheduled_at || item.created_at) }}</span>
							</div>
						</div>
					</div>

					<div v-else-if="!loadingEmailQueue" class="empty-state">
						<h3 class="empty-title">No Email Queue Items</h3>
						<p class="empty-description">Queue entries will appear here after auto or sequenced campaigns generate email work.</p>
					</div>
				</div>
			</div>

			<!-- Cron Tab -->
			<div v-if="activeTab === 'cron'" class="tab-pane active">
				<div class="settings-section">
					<div class="empty-state">
						<h3 class="empty-title">Cron Settings</h3>
						<p class="empty-description">Configure cron job schedules for auto campaigns.</p>
					</div>
				</div>
			</div>

			<!-- Notifications Tab -->
			<div v-if="activeTab === 'notifications'" class="tab-pane active">
				<div class="settings-section">
					<div class="empty-state">
						<h3 class="empty-title">Notification Settings</h3>
						<p class="empty-description">Configure notification preferences for auto campaigns.</p>
					</div>
				</div>
			</div>

			<!-- Statistics Tab -->
			<div v-if="activeTab === 'statistics'" class="tab-pane active">
				<div class="statistics-content">
					<!-- Overview Stats Cards -->
					<div class="stats-grid mb-4">
						<div class="stat-card">
							<div class="stat-icon campaigns">
								<i class="fad fa-bullhorn"></i>
							</div>
							<div class="stat-content">
								<h3 class="stat-number">{{ statistics.totalCampaigns || 0 }}</h3>
								<p class="stat-label">Total Campaigns</p>
								<div class="stat-change positive">
									<i class="fad fa-arrow-up"></i>
									+12% this month
								</div>
							</div>
						</div>

						<div class="stat-card">
							<div class="stat-icon triggers">
								<i class="fad fa-bolt"></i>
							</div>
							<div class="stat-content">
								<h3 class="stat-number">{{ statistics.activeTriggers || 0 }}</h3>
								<p class="stat-label">Active Triggers</p>
								<div class="stat-change neutral">
									<i class="fad fa-minus"></i>
									No change
								</div>
							</div>
						</div>

						<div class="stat-card">
							<div class="stat-icon deliveries">
								<i class="fad fa-paper-plane"></i>
							</div>
							<div class="stat-content">
								<h3 class="stat-number">{{ statistics.totalDeliveries || 0 }}</h3>
								<p class="stat-label">Total Deliveries</p>
								<div class="stat-change positive">
									<i class="fad fa-arrow-up"></i>
									+8% this week
								</div>
							</div>
						</div>

						<div class="stat-card">
							<div class="stat-icon success-rate">
								<i class="fad fa-chart-line"></i>
							</div>
							<div class="stat-content">
								<h3 class="stat-number">{{ statistics.successRate || 0 }}%</h3>
								<p class="stat-label">Success Rate</p>
								<div class="stat-change positive">
									<i class="fad fa-arrow-up"></i>
									+2.1% avg
								</div>
							</div>
						</div>
					</div>

					<!-- Charts Row -->
					<div class="row mb-4">
						<div class="col-lg-8">
							<div class="chart-card">
								<div class="chart-header">
									<h4 class="chart-title">Campaign Activity</h4>
									<p class="chart-subtitle">Last 30 days</p>
								</div>
								<div class="chart-container">
									<canvas id="campaignActivityChart" width="600" height="300"></canvas>
								</div>
							</div>
						</div>
						<div class="col-lg-4">
							<div class="chart-card">
								<div class="chart-header">
									<h4 class="chart-title">Campaign Types</h4>
									<p class="chart-subtitle">Distribution</p>
								</div>
								<div class="chart-container">
									<canvas id="campaignTypesChart" width="300" height="300"></canvas>
								</div>
							</div>
						</div>
					</div>

					<!-- Developer Info -->
					<div class="row">
						<div class="col-lg-6">
							<div class="dev-info-card">
								<div class="dev-header">
									<h4 class="dev-title">
										<i class="fad fa-code me-2"></i>
										API Performance
									</h4>
								</div>
								<div class="dev-metrics">
									<div class="metric-row">
										<span class="metric-label">Average Response Time</span>
										<span class="metric-value">{{ statistics.avgResponseTime || '---' }}ms</span>
									</div>
									<div class="metric-row">
										<span class="metric-label">API Calls Today</span>
										<span class="metric-value">{{ statistics.apiCallsToday || 0 }}</span>
									</div>
									<div class="metric-row">
										<span class="metric-label">Error Rate</span>
										<span class="metric-value">{{ statistics.errorRate || '0.0' }}%</span>
									</div>
									<div class="metric-row">
										<span class="metric-label">Cache Hit Rate</span>
										<span class="metric-value">{{ statistics.cacheHitRate || '---' }}%</span>
									</div>
								</div>
							</div>
						</div>
						<div class="col-lg-6">
							<div class="dev-info-card">
								<div class="dev-header">
									<h4 class="dev-title">
										<i class="fad fa-database me-2"></i>
										System Status
									</h4>
								</div>
								<div class="dev-metrics">
									<div class="metric-row">
										<span class="metric-label">Database Connection</span>
										<span class="metric-value status online">
											<i class="fad fa-circle"></i>
											Online
										</span>
									</div>
									<div class="metric-row">
										<span class="metric-label">LOB API Status</span>
										<span class="metric-value status online">
											<i class="fad fa-circle"></i>
											Connected
										</span>
									</div>
									<div class="metric-row">
										<span class="metric-label">Queue Size</span>
										<span class="metric-value">{{ statistics.queueSize || 0 }}</span>
									</div>
									<div class="metric-row">
										<span class="metric-label">Last Sync</span>
										<span class="metric-value">{{ statistics.lastSync || 'Never' }}</span>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- Save Button -->
		<div class="settings-footer mt-5">
			<div class="d-flex justify-content-end">
				<button class="save-btn" @click="saveSettings" title="Save Settings">
					<i class="fad fa-save save-icon me-2"></i>
					Save
				</button>
			</div>
		</div>

		<!-- Create/Edit Trigger Offcanvas -->
		<div class="offcanvas offcanvas-end" tabindex="-1" id="createTriggerOffcanvas" aria-labelledby="createTriggerOffcanvasLabel">
			<div class="offcanvas-header">
				<h5 class="offcanvas-title" id="createTriggerOffcanvasLabel">{{ isEditingTrigger ? 'Edit Trigger' : 'Create Trigger' }}</h5>
				<button type="button" class="btn-close" data-bs-dismiss="offcanvas" aria-label="Close"></button>
			</div>
			<div class="offcanvas-body p-0">
				<!-- Section 1: Main Details -->
				<div class="trigger-section">
					<div class="section-header">
						<h6 class="section-title">Main Details</h6>
						<p class="section-description">Basic trigger information</p>
					</div>
					<div class="section-content">
						<div class="mb-3">
							<label class="form-label">Trigger Name *</label>
							<input type="text" class="form-control" v-model="currentTrigger.name" placeholder="Enter trigger name" required />
						</div>
						<div class="mb-3">
							<label class="form-label">Reason</label>
							<textarea class="form-control" v-model="currentTrigger.reason" rows="3" placeholder="Describe the purpose of this trigger..."></textarea>
						</div>
					</div>
				</div>

				<!-- Section 2: Trigger Type -->
				<div class="trigger-section">
					<div class="section-header">
						<h6 class="section-title">Trigger Type</h6>
						<p class="section-description">Configure how this trigger activates</p>
					</div>
					<div class="section-content">
						<div class="mb-3">
							<label class="form-label">Hook</label>
							<select class="form-select" v-model="currentTrigger.hook">
								<option value="">No hook — manual only</option>
								<option v-for="h in knownHooks" :key="h.hook" :value="h.hook">{{ h.label }} — {{ h.hook }}</option>
								<option v-if="currentTrigger.hook && !knownHooks.some(h => h.hook === currentTrigger.hook)" :value="currentTrigger.hook">{{ currentTrigger.hook }} (not wired in mc2-api)</option>
							</select>
							<div class="form-text">Incoming webhook that fires this trigger's campaigns. Without a hook the trigger can still be fired manually from the Auto Campaigns home (Developer tools).</div>
						</div>
						<div class="mb-3">
							<label class="form-label">Unique URL fallback destination</label>
							<input type="url" class="form-control" v-model="currentTrigger.unique_url_domain" placeholder="https://townweb.com" />
							<div class="form-text">Where short links send visitors when a campaign enables unique URLs but does not set its own destination. Printed links look like <code>twn.link/a7k2m</code> and redirect here with UTM params.</div>
						</div>
					</div>
				</div>

				<!-- Section 3: Conditions -->
				<div class="trigger-section">
					<div class="section-header">
						<h6 class="section-title">Conditions</h6>
						<p class="section-description">Configure targeting conditions and filters</p>
					</div>
					<div class="section-content">
						<div class="mb-3">
							<div class="form-check">
								<input class="form-check-input" type="checkbox" v-model="useRadius" id="useRadiusFilter" />
								<label class="form-check-label" for="useRadiusFilter">
									Use Radius Filter
								</label>
							</div>
						</div>

						<div v-if="useRadius" class="mb-3">
							<small class="text-muted">If enabled, the HOOK in request must include a <code>municipality_id</code> to apply the radius filter to it.</small>
							<br />
							<label class="form-label">Radius (miles)</label>

							<input type="number" class="form-control" v-model="radiusSize" placeholder="25" min="1" max="1000" />
							<div class="form-text">Target municipalities within this radius</div>
						</div>

						<!-- Additional Radius Conditions -->
						<div v-if="useRadius" class="mt-4">
							<div class="additional-filters-header">
								<h6 class="additional-filters-title">Additional Filters</h6>
								<p class="additional-filters-description">Apply additional targeting conditions within the radius</p>
							</div>

							<div class="additional-filters-container">
								<!-- Geographic Filters -->
								<div class="filter-group">
									<h6 class="filter-group-title">Geographic</h6>
									<div class="filter-options">
										<div class="filter-option">
											<input class="form-check-input" type="checkbox" v-model="conditions.sameState" id="sameState" />
											<label class="filter-label" for="sameState">
												<i class="fad fa-map-marker-alt filter-icon"></i>
												Same State
											</label>
										</div>
										<div class="filter-option">
											<input class="form-check-input" type="checkbox" v-model="conditions.sameCounty" id="sameCounty" />
											<label class="filter-label" for="sameCounty">
												<i class="fad fa-map filter-icon"></i>
												Same County
											</label>
										</div>
									</div>
									<div class="mt-3">
										<label class="form-label-small d-block mb-2">Recipient States</label>
										<div class="state-filter-actions mb-2">
											<button type="button" class="btn btn-sm btn-link p-0 me-3" @click="selectAllStates">Select all</button>
											<button type="button" class="btn btn-sm btn-link p-0" @click="conditions.states = []">Clear</button>
										</div>
										<div class="state-filter-grid">
											<label v-for="state in usStates" :key="state.abbreviation" class="state-filter-option">
												<input class="form-check-input" type="checkbox" :value="state.abbreviation" v-model="conditions.states" />
												<span>{{ state.name }} ({{ state.abbreviation }})</span>
											</label>
										</div>
										<small class="form-text text-muted d-block mt-2">Leave empty to allow municipalities from every state.</small>
									</div>
								</div>

								<!-- Client Type Filters -->
								<div class="filter-group">
									<h6 class="filter-group-title">Include Clients</h6>
									<div class="filter-options">
										<div class="filter-option">
											<input class="form-check-input" type="checkbox" v-model="conditions.twClient" id="twClient" />
											<label class="filter-label" for="twClient">
												<i class="fad fa-building filter-icon text-success"></i>
												TW Client
											</label>
										</div>
										<div class="filter-option">
											<input class="form-check-input" type="checkbox" v-model="conditions.hgClient" id="hgClient" />
											<label class="filter-label" for="hgClient">
												<i class="fad fa-building filter-icon text-info"></i>
												HG Client
											</label>
										</div>
										<div class="filter-option">
											<input class="form-check-input" type="checkbox" v-model="conditions.clerkMinutesClient" id="clerkMinutesClient" />
											<label class="filter-label" for="clerkMinutesClient">
												<i class="fad fa-file-alt filter-icon text-warning"></i>
												ClerkMinutes Client
											</label>
										</div>
									</div>
								</div>

								<!-- Exclude Clients -->
								<div class="filter-group">
									<h6 class="filter-group-title">Exclude Clients</h6>
									<div class="filter-options">
										<div class="filter-option">
											<input class="form-check-input" type="checkbox" v-model="conditions.notTwClient" id="notTwClient" />
											<label class="filter-label" for="notTwClient">
												<i class="fad fa-building-slash filter-icon text-danger"></i>
												NOT TW Client
											</label>
										</div>
										<div class="filter-option">
											<input class="form-check-input" type="checkbox" v-model="conditions.notHgClient" id="notHgClient" />
											<label class="filter-label" for="notHgClient">
												<i class="fad fa-building-slash filter-icon text-danger"></i>
												NOT HG Client
											</label>
										</div>
										<div class="filter-option">
											<input class="form-check-input" type="checkbox" v-model="conditions.notClerkMinutesClient" id="notClerkMinutesClient" />
											<label class="filter-label" for="notClerkMinutesClient">
												<i class="fad fa-file-slash filter-icon text-danger"></i>
												NOT ClerkMinutes Client
											</label>
										</div>
									</div>
								</div>

								<!-- Campaign History -->
								<div class="filter-group">
									<h6 class="filter-group-title">Campaign History</h6>
									<div class="filter-options">
										<div class="filter-option">
											<input class="form-check-input" type="checkbox" v-model="conditions.notReceivedCampaignLast4Weeks" id="notReceivedCampaignLast4Weeks" />
											<label class="filter-label" for="notReceivedCampaignLast4Weeks">
												<i class="fad fa-clock filter-icon text-secondary"></i>
												Not Received Campaign in Last 4 Weeks
											</label>
										</div>
									</div>
								</div>

								<!-- Population Range -->
								<div class="filter-group">
									<h6 class="filter-group-title">Population Range</h6>
									<div class="population-range-inputs">
										<div class="row g-3">
											<div class="col-6">
												<label for="populationFrom" class="form-label-small">From</label>
												<input
													type="number"
													class="form-control form-control-sm"
													id="populationFrom"
													v-model.number="conditions.population_from"
													placeholder="e.g., 5000"
													min="0"
												/>
											</div>
											<div class="col-6">
												<label for="populationTo" class="form-label-small">To</label>
												<input
													type="number"
													class="form-control form-control-sm"
													id="populationTo"
													v-model.number="conditions.population_to"
													placeholder="e.g., 50000"
													min="0"
												/>
											</div>
										</div>
										<small class="form-text text-muted mt-2 d-block">
											<i class="fad fa-info-circle me-1"></i>
											Both fields are required if filtering by population
										</small>
									</div>
								</div>
							</div>
						</div>

						<!-- Text Pattern Data Source -->
						<div class="mt-4">
							<div class="form-group">
								<label class="form-label" for="patternDataSource">Text Pattern Data Source</label>
								<select class="form-select" v-model="patternDataFrom" id="patternDataSource">
									<option value="">Select data source</option>
									<option value="hook_incoming_municipality">From Hook Incoming Municipality</option>
									<option value="item_data">From Item Data</option>
								</select>
								<small class="form-text text-muted">Choose the source for text pattern data in campaigns</small>
							</div>
						</div>
					</div>
				</div>

				<!-- Footer Actions -->
				<div class="offcanvas-footer">
					<button type="button" class="btn btn-outline-secondary" data-bs-dismiss="offcanvas">Cancel</button>
					<button type="button" class="btn btn-primary" @click="saveTrigger" :disabled="!canSaveTrigger">
						<i :class="['fad', savingTrigger ? 'fa-spinner fa-spin' : 'fa-save']"></i>
						{{ isEditingTrigger ? 'Update' : 'Create' }} Trigger
					</button>
				</div>
			</div>
		</div>

		<!-- Create/Edit Sender Offcanvas -->
		<div class="offcanvas offcanvas-end" tabindex="-1" id="createSenderOffcanvas" aria-labelledby="createSenderOffcanvasLabel">
			<div class="offcanvas-header">
				<h5 class="offcanvas-title" id="createSenderOffcanvasLabel">{{ isEditingSender ? 'Edit Sender' : 'Add Sender' }}</h5>
				<button type="button" class="btn-close" data-bs-dismiss="offcanvas" aria-label="Close"></button>
			</div>
			<div class="offcanvas-body p-0">
				<!-- Section 1: Sender Details -->
				<div class="trigger-section">
					<div class="section-header">
						<h6 class="section-title">Sender Information</h6>
						<p class="section-description">Basic sender details</p>
					</div>
					<div class="section-content">
						<div class="mb-3">
							<label class="form-label">Sender Name *</label>
							<input type="text" class="form-control" v-model="currentSender.name" placeholder="Enter sender name" required />
						</div>
					</div>
				</div>

				<!-- Section 2: Address Information -->
				<div class="trigger-section">
					<div class="section-header">
						<h6 class="section-title">Address Information</h6>
						<p class="section-description">Sender's address details</p>
					</div>
					<div class="section-content">
						<div class="mb-3">
							<label class="form-label">Street Address *</label>
							<input type="text" class="form-control" v-model="currentSender.address_line1" placeholder="Enter street address" required />
						</div>
						<div class="row">
							<div class="col-md-6">
								<div class="mb-3">
									<label class="form-label">City *</label>
									<input type="text" class="form-control" v-model="currentSender.address_city" placeholder="Enter city" required />
								</div>
							</div>
							<div class="col-md-3">
								<div class="mb-3">
									<label class="form-label">State *</label>
									<input type="text" class="form-control" v-model="currentSender.address_state" placeholder="State" required maxlength="2" />
								</div>
							</div>
							<div class="col-md-3">
								<div class="mb-3">
									<label class="form-label">ZIP Code *</label>
									<input type="text" class="form-control" v-model="currentSender.address_zip" placeholder="ZIP" required />
								</div>
							</div>
						</div>
					</div>
				</div>

				<!-- Footer Actions -->
				<div class="offcanvas-footer">
					<button type="button" class="btn btn-outline-secondary" data-bs-dismiss="offcanvas">Cancel</button>
					<button type="button" class="btn btn-primary" @click="saveSender" :disabled="!canSaveSender">
						<i :class="['fad', savingSender ? 'fa-spinner fa-spin' : 'fa-save']"></i>
						{{ isEditingSender ? 'Update' : 'Add' }} Sender
					</button>
				</div>
			</div>
		</div>

		<!-- Create/Edit Receiver Offcanvas -->
		<div class="offcanvas offcanvas-end" tabindex="-1" id="createReceiverOffcanvas" aria-labelledby="createReceiverOffcanvasLabel">
			<div class="offcanvas-header">
				<h5 class="offcanvas-title" id="createReceiverOffcanvasLabel">{{ isEditingReceiver ? 'Edit Receiver' : 'Add Receiver' }}</h5>
				<button type="button" class="btn-close" data-bs-dismiss="offcanvas" aria-label="Close"></button>
			</div>
			<div class="offcanvas-body p-0">
				<!-- Section 1: Receiver Details -->
				<div class="trigger-section">
					<div class="section-header">
						<h6 class="section-title">Receiver Information</h6>
						<p class="section-description">Who the extra copy is addressed to</p>
					</div>
					<div class="section-content">
						<div class="mb-3">
							<label class="form-label">Receiver Name *</label>
							<input type="text" class="form-control" v-model="currentReceiver.name" placeholder="Enter receiver name" required />
						</div>
						<div class="form-check form-switch">
							<input class="form-check-input" type="checkbox" id="receiverEnabled" v-model="currentReceiver.enabled" />
							<label class="form-check-label" for="receiverEnabled">
								Enabled — included in every campaign created from now on
							</label>
						</div>
					</div>
				</div>

				<!-- Section 2: Delivery Schedule -->
				<div class="trigger-section">
					<div class="section-header">
						<h6 class="section-title">Delivery Schedule</h6>
						<p class="section-description">When LOB mails this receiver's copy</p>
					</div>
					<div class="section-content">
						<div class="form-check form-switch">
							<input class="form-check-input" type="checkbox" id="receiverBatched" v-model="receiverBatched" />
							<label class="form-check-label" for="receiverBatched">
								Send on a fixed weekday — copies of every campaign wait in LOB and go to print together
							</label>
						</div>
						<div v-if="receiverBatched" class="mt-3">
							<label class="form-label">Send Day</label>
							<select class="form-select" v-model="currentReceiver.send_day">
								<option v-for="day in receiverSendDays" :key="day" :value="day">{{ day | capitalize }}</option>
							</select>
							<small class="form-text text-muted">
								Copies created on that day go out right away. Only the receiver's copy waits — the campaign itself is mailed as usual.
							</small>
						</div>
						<small v-else class="form-text text-muted">Off — the copy is mailed together with each campaign.</small>
					</div>
				</div>

				<!-- Section 3: Address Information -->
				<div class="trigger-section">
					<div class="section-header">
						<h6 class="section-title">Address Information</h6>
						<p class="section-description">Mailing address, entered by hand — not tied to a municipality</p>
					</div>
					<div class="section-content">
						<div class="mb-3">
							<label class="form-label">Street Address *</label>
							<input type="text" class="form-control" v-model="currentReceiver.address" placeholder="Enter street address" required />
						</div>
						<div class="row">
							<div class="col-md-6">
								<div class="mb-3">
									<label class="form-label">City *</label>
									<input type="text" class="form-control" v-model="currentReceiver.city" placeholder="Enter city" required />
								</div>
							</div>
							<div class="col-md-3">
								<div class="mb-3">
									<label class="form-label">State *</label>
									<input type="text" class="form-control" v-model="currentReceiver.state" placeholder="State" required maxlength="2" />
								</div>
							</div>
							<div class="col-md-3">
								<div class="mb-3">
									<label class="form-label">ZIP Code *</label>
									<input type="text" class="form-control" v-model="currentReceiver.zip" placeholder="ZIP" required />
								</div>
							</div>
						</div>
					</div>
				</div>

				<!-- Footer Actions -->
				<div class="offcanvas-footer">
					<button type="button" class="btn btn-outline-secondary" data-bs-dismiss="offcanvas">Cancel</button>
					<button type="button" class="btn btn-primary" @click="saveReceiver" :disabled="!canSaveReceiver">
						<i :class="['fad', savingReceiver ? 'fa-spinner fa-spin' : 'fa-save']"></i>
						{{ isEditingReceiver ? 'Update' : 'Add' }} Receiver
					</button>
				</div>
			</div>
		</div>

		<div class="offcanvas offcanvas-end" tabindex="-1" id="createEmailProviderOffcanvas" aria-labelledby="createEmailProviderOffcanvasLabel">
			<div class="offcanvas-header">
				<h5 class="offcanvas-title" id="createEmailProviderOffcanvasLabel">{{ isEditingEmailProvider ? 'Edit Email Provider' : 'Add Email Provider' }}</h5>
				<button type="button" class="btn-close" data-bs-dismiss="offcanvas" aria-label="Close"></button>
			</div>
			<div class="offcanvas-body p-0">
				<div class="trigger-section">
					<div class="section-header">
						<h6 class="section-title">Provider Details</h6>
						<p class="section-description">Name, provider type and active state</p>
					</div>
					<div class="section-content">
						<div class="mb-3">
							<label class="form-label">Config Name *</label>
							<input type="text" class="form-control" v-model="currentEmailProvider.name" placeholder="Production SendGrid" />
						</div>
						<div class="mb-3">
							<label class="form-label">Provider *</label>
							<select class="form-select" v-model="currentEmailProvider.provider">
								<option value="sendgrid">SendGrid</option>
								<option value="postmark">Postmark</option>
								<option value="instantly">Instantly</option>
							</select>
						</div>
						<div class="mb-3">
							<label class="form-label">Status</label>
							<select class="form-select" v-model="currentEmailProvider.status">
								<option value="active">Active</option>
								<option value="inactive">Inactive</option>
							</select>
						</div>
					</div>
				</div>
				<div class="trigger-section">
					<div class="section-header">
						<h6 class="section-title">Sender Defaults</h6>
						<p class="section-description">Default from/reply-to information for the provider</p>
					</div>
					<div class="section-content">
						<div class="mb-3">
							<label class="form-label">Default From Name</label>
							<input type="text" class="form-control" v-model="currentEmailProvider.default_from_name" placeholder="Town Web" />
						</div>
						<div class="mb-3">
							<label class="form-label">Default From Email</label>
							<input type="email" class="form-control" v-model="currentEmailProvider.default_from_email" placeholder="hello@example.com" />
						</div>
						<div class="mb-3">
							<label class="form-label">Default Reply-To</label>
							<input type="email" class="form-control" v-model="currentEmailProvider.default_reply_to" placeholder="support@example.com" />
						</div>
					</div>
				</div>
				<div class="trigger-section">
					<div class="section-header">
						<h6 class="section-title">Provider Settings</h6>
						<p class="section-description">Provider-specific credentials and options</p>
					</div>
					<div class="section-content">
						<div class="mb-3">
							<label class="form-label">API Key / Token</label>
							<input type="text" class="form-control" v-model="currentEmailProvider.settings.api_key" placeholder="API key or bearer token" />
						</div>
						<div class="mb-3" v-if="currentEmailProvider.provider === 'postmark'">
							<label class="form-label">Postmark Server Token</label>
							<input type="text" class="form-control" v-model="currentEmailProvider.settings.server_token" placeholder="Postmark server token" />
						</div>
						<div class="mb-3">
							<label class="form-label">Base URL</label>
							<input type="text" class="form-control" v-model="currentEmailProvider.settings.base_url" placeholder="Optional override" />
						</div>
						<div class="mb-3" v-if="currentEmailProvider.provider === 'postmark'">
							<label class="form-label">Message Stream</label>
							<input type="text" class="form-control" v-model="currentEmailProvider.settings.message_stream" placeholder="outbound" />
						</div>
					</div>
				</div>
				<div class="trigger-section">
					<div class="section-header">
						<h6 class="section-title">Webhook Security</h6>
						<p class="section-description">Configure verification for provider webhook callbacks</p>
					</div>
					<div class="section-content">
						<div class="mb-3" v-if="currentEmailProvider.provider === 'sendgrid'">
							<label class="form-label">SendGrid Webhook Public Key</label>
							<textarea
								class="form-control"
								rows="4"
								v-model="currentEmailProvider.settings.webhook_public_key"
								placeholder="Paste the SendGrid Event Webhook verification public key"
							></textarea>
						</div>
						<div class="mb-3">
							<label class="form-label">Shared Webhook Secret</label>
							<input
								type="text"
								class="form-control"
								v-model="currentEmailProvider.settings.webhook_secret"
								placeholder="Optional shared secret for query param or header verification"
							/>
							<div class="form-text">Use `?secret=...` in the webhook URL or send `X-Campaign-Webhook-Secret`.</div>
						</div>
					</div>
				</div>
				<div class="offcanvas-footer">
					<button type="button" class="btn btn-outline-secondary" data-bs-dismiss="offcanvas">Cancel</button>
					<button type="button" class="btn btn-primary" @click="saveEmailProvider" :disabled="!canSaveEmailProvider">
						<i :class="['fad', savingEmailProvider ? 'fa-spinner fa-spin' : 'fa-save']"></i>
						{{ isEditingEmailProvider ? 'Update' : 'Create' }} Provider
					</button>
				</div>
			</div>
		</div>

		<div class="offcanvas offcanvas-end" tabindex="-1" id="createEmailTemplateOffcanvas" aria-labelledby="createEmailTemplateOffcanvasLabel">
			<div class="offcanvas-header">
				<h5 class="offcanvas-title" id="createEmailTemplateOffcanvasLabel">{{ isEditingEmailTemplate ? 'Edit Email Template' : 'Add Email Template' }}</h5>
				<button type="button" class="btn-close" data-bs-dismiss="offcanvas" aria-label="Close"></button>
			</div>
			<div class="offcanvas-body p-0">
				<div class="trigger-section">
					<div class="section-header">
						<h6 class="section-title">Template Metadata</h6>
						<p class="section-description">Basic information and status</p>
					</div>
					<div class="section-content">
						<div class="mb-3">
							<label class="form-label">Template Name *</label>
							<input type="text" class="form-control" v-model="currentEmailTemplate.name" placeholder="Welcome Follow-up" />
						</div>
						<div class="mb-3">
							<label class="form-label">Code *</label>
							<input type="text" class="form-control" v-model="currentEmailTemplate.code" placeholder="welcome_followup" />
						</div>
						<div class="mb-3">
							<label class="form-label">Status</label>
							<select class="form-select" v-model="currentEmailTemplate.status">
								<option value="draft">Draft</option>
								<option value="active">Active</option>
								<option value="archived">Archived</option>
							</select>
						</div>
						<div class="mb-3">
							<label class="form-label">Description</label>
							<textarea class="form-control" rows="3" v-model="currentEmailTemplate.description" placeholder="What this template is for"></textarea>
						</div>
					</div>
				</div>
				<div class="trigger-section">
					<div class="section-header">
						<h6 class="section-title">Content</h6>
						<p class="section-description">Subject, preheader and email body</p>
					</div>
					<div class="section-content">
						<div class="mb-3">
							<label class="form-label">Subject *</label>
							<input type="text" class="form-control" v-model="currentEmailTemplate.subject" placeholder="Hello municipality_name" />
						</div>
						<div class="mb-3">
							<label class="form-label">Preheader</label>
							<input type="text" class="form-control" v-model="currentEmailTemplate.preheader" placeholder="Optional preview text" />
						</div>
						<div class="mb-3">
							<label class="form-label">HTML Content *</label>
							<textarea class="form-control" rows="8" v-model="currentEmailTemplate.html_content" placeholder="<p>Hello municipality_name</p>"></textarea>
						</div>
						<div class="mb-3">
							<label class="form-label">Text Content</label>
							<textarea class="form-control" rows="5" v-model="currentEmailTemplate.text_content" placeholder="Hello municipality_name"></textarea>
						</div>
					</div>
				</div>
				<div class="trigger-section">
					<div class="section-header">
						<h6 class="section-title">Optional Defaults</h6>
						<p class="section-description">Per-template sender overrides and grouping</p>
					</div>
					<div class="section-content">
						<div class="mb-3">
							<label class="form-label">From Name</label>
							<input type="text" class="form-control" v-model="currentEmailTemplate.from_name" placeholder="Town Web" />
						</div>
						<div class="mb-3">
							<label class="form-label">From Email</label>
							<input type="email" class="form-control" v-model="currentEmailTemplate.from_email" placeholder="hello@example.com" />
						</div>
						<div class="mb-3">
							<label class="form-label">Reply-To Email</label>
							<input type="email" class="form-control" v-model="currentEmailTemplate.reply_to_email" placeholder="support@example.com" />
						</div>
						<div class="mb-3">
							<label class="form-label">Category</label>
							<input type="text" class="form-control" v-model="currentEmailTemplate.category" placeholder="onboarding" />
						</div>
					</div>
				</div>
				<div class="offcanvas-footer">
					<button type="button" class="btn btn-outline-secondary" data-bs-dismiss="offcanvas">Cancel</button>
					<button type="button" class="btn btn-primary" @click="saveEmailTemplate" :disabled="!canSaveEmailTemplate">
						<i :class="['fad', savingEmailTemplate ? 'fa-spinner fa-spin' : 'fa-save']"></i>
						{{ isEditingEmailTemplate ? 'Update' : 'Create' }} Template
					</button>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import restApi from '@/api/index.js'
import { Offcanvas } from 'bootstrap'
import Chart from 'chart.js/auto'

export default {
	name: 'AutoCampaignSettings',
	data() {
		return {
			activeTab: 'main',
			// LOB Settings
			lobSettings: null,
			savingLobSettings: false,
			// Triggers Management
			triggers: [],
			knownHooks: [],
			loadingTriggers: false,
			isEditingTrigger: false,
			savingTrigger: false,
			currentTrigger: {
				id: null,
				name: '',
				reason: '',
				trigger_date: '',
				hook: '',
				conditions: null,
				filters: null,
				unique_url_domain: '',
			},
			useRadius: false,
			radiusSize: '',
			patternDataFrom: '',
			conditions: {
				sameState: false,
				sameCounty: false,
				states: [],
				twClient: false,
				hgClient: false,
				notTwClient: false,
				notHgClient: false,
				clerkMinutesClient: false,
				notClerkMinutesClient: false,
				notReceivedCampaignLast4Weeks: false,
				population_from: null,
				population_to: null,
			},
			usStates: [
				{ abbreviation: 'AL', name: 'Alabama' }, { abbreviation: 'AK', name: 'Alaska' }, { abbreviation: 'AZ', name: 'Arizona' }, { abbreviation: 'AR', name: 'Arkansas' },
				{ abbreviation: 'CA', name: 'California' }, { abbreviation: 'CO', name: 'Colorado' }, { abbreviation: 'CT', name: 'Connecticut' }, { abbreviation: 'DE', name: 'Delaware' },
				{ abbreviation: 'DC', name: 'District of Columbia' }, { abbreviation: 'FL', name: 'Florida' }, { abbreviation: 'GA', name: 'Georgia' }, { abbreviation: 'HI', name: 'Hawaii' },
				{ abbreviation: 'ID', name: 'Idaho' }, { abbreviation: 'IL', name: 'Illinois' }, { abbreviation: 'IN', name: 'Indiana' }, { abbreviation: 'IA', name: 'Iowa' },
				{ abbreviation: 'KS', name: 'Kansas' }, { abbreviation: 'KY', name: 'Kentucky' }, { abbreviation: 'LA', name: 'Louisiana' }, { abbreviation: 'ME', name: 'Maine' },
				{ abbreviation: 'MD', name: 'Maryland' }, { abbreviation: 'MA', name: 'Massachusetts' }, { abbreviation: 'MI', name: 'Michigan' }, { abbreviation: 'MN', name: 'Minnesota' },
				{ abbreviation: 'MS', name: 'Mississippi' }, { abbreviation: 'MO', name: 'Missouri' }, { abbreviation: 'MT', name: 'Montana' }, { abbreviation: 'NE', name: 'Nebraska' },
				{ abbreviation: 'NV', name: 'Nevada' }, { abbreviation: 'NH', name: 'New Hampshire' }, { abbreviation: 'NJ', name: 'New Jersey' }, { abbreviation: 'NM', name: 'New Mexico' },
				{ abbreviation: 'NY', name: 'New York' }, { abbreviation: 'NC', name: 'North Carolina' }, { abbreviation: 'ND', name: 'North Dakota' }, { abbreviation: 'OH', name: 'Ohio' },
				{ abbreviation: 'OK', name: 'Oklahoma' }, { abbreviation: 'OR', name: 'Oregon' }, { abbreviation: 'PA', name: 'Pennsylvania' }, { abbreviation: 'RI', name: 'Rhode Island' },
				{ abbreviation: 'SC', name: 'South Carolina' }, { abbreviation: 'SD', name: 'South Dakota' }, { abbreviation: 'TN', name: 'Tennessee' }, { abbreviation: 'TX', name: 'Texas' },
				{ abbreviation: 'UT', name: 'Utah' }, { abbreviation: 'VT', name: 'Vermont' }, { abbreviation: 'VA', name: 'Virginia' }, { abbreviation: 'WA', name: 'Washington' },
				{ abbreviation: 'WV', name: 'West Virginia' }, { abbreviation: 'WI', name: 'Wisconsin' }, { abbreviation: 'WY', name: 'Wyoming' },
			],
			$createTriggerOffcanvas: null,
			// Senders Management
			senders: [],
			loadingSenders: false,
			isEditingSender: false,
			savingSender: false,
			editingSenderIndex: null,
			currentSender: {
				name: '',
				address_line1: '',
				address_city: '',
				address_state: '',
				address_zip: '',
			},
			$createSenderOffcanvas: null,
			// Always-On Receivers Management (stored alongside senders in LobSettings)
			receivers: [],
			isEditingReceiver: false,
			savingReceiver: false,
			editingReceiverIndex: null,
			currentReceiver: {
				name: '',
				address: '',
				city: '',
				state: '',
				zip: '',
				enabled: true,
				send_day: '',
			},
			// Weekdays a receiver's copies can be batched to (LOB does not print on weekends)
			receiverSendDays: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
			$createReceiverOffcanvas: null,
			// Email Provider Configs
			emailProviderConfigs: [],
			loadingEmailProviders: false,
			isEditingEmailProvider: false,
			savingEmailProvider: false,
			currentEmailProvider: {
				id: null,
				name: '',
				provider: 'sendgrid',
				status: 'active',
				default_from_name: '',
				default_from_email: '',
				default_reply_to: '',
				settings: {
					api_key: '',
					server_token: '',
					base_url: '',
					message_stream: '',
					webhook_public_key: '',
					webhook_secret: '',
				},
			},
			$createEmailProviderOffcanvas: null,
			// Email Templates
			emailTemplates: [],
			loadingEmailTemplates: false,
			isEditingEmailTemplate: false,
			savingEmailTemplate: false,
			currentEmailTemplate: {
				id: null,
				name: '',
				code: '',
				description: '',
				subject: '',
				preheader: '',
				html_content: '',
				text_content: '',
				from_name: '',
				from_email: '',
				reply_to_email: '',
				category: '',
				status: 'draft',
			},
			$createEmailTemplateOffcanvas: null,
			// Email Queue
			emailQueueItems: [],
			emailQueueMeta: {
				total: 0,
			},
			loadingEmailQueue: false,
			processingScheduledEmails: false,
			emailQueueFilters: {
				campaign_source: '',
				status: '',
			},
			// Statistics
			statistics: {
				totalCampaigns: 0,
				activeTriggers: 0,
				totalDeliveries: 0,
				successRate: 0,
				avgResponseTime: 0,
				apiCallsToday: 0,
				errorRate: 0,
				cacheHitRate: 0,
				queueSize: 0,
				lastSync: null,
			},
			loadingStatistics: false,
		}
	},
	filters: {
		capitalize(value) {
			if (!value) return ''
			value = value.toString()
			return value.charAt(0).toUpperCase() + value.slice(1)
		},
	},
	created() {
		// Initialize active tab from URL query parameter
		const urlTab = this.$route.query.tab
		if (
			urlTab &&
			['main', 'triggers', 'senders', 'receivers', 'emailProviders', 'emailTemplates', 'emailQueue', 'cron', 'notifications', 'statistics'].includes(urlTab)
		) {
			this.activeTab = urlTab
		}

		console.log('Component created, initial triggers:', this.triggers)
		this.fetchTriggers()
		this.fetchKnownHooks()
		this.getLobSettings()
		this.fetchEmailProviders()
		this.fetchEmailTemplates()
		this.fetchEmailQueue()
		this.loadStatistics()
	},
	mounted() {
		this.$createTriggerOffcanvas = new Offcanvas('#createTriggerOffcanvas')
		this.$createSenderOffcanvas = new Offcanvas('#createSenderOffcanvas')
		this.$createReceiverOffcanvas = new Offcanvas('#createReceiverOffcanvas')
		this.$createEmailProviderOffcanvas = new Offcanvas('#createEmailProviderOffcanvas')
		this.$createEmailTemplateOffcanvas = new Offcanvas('#createEmailTemplateOffcanvas')
		console.log('Component mounted, triggers count:', this.triggers.length)

		// Test API connection
		console.log('RestAPI available:', !!restApi)
		console.log('Testing API endpoint...')
		restApi
			.get('/auto-campaign-triggers')
			.then(response => {
				console.log('Manual API test successful:', response)
			})
			.catch(error => {
				console.log('Manual API test failed:', error)
			})
	},
	computed: {
		canSaveTrigger() {
			const hasName = this.currentTrigger.name.trim() !== ''
			return hasName
		},
		canSaveSender() {
			return (
				this.currentSender.name.trim() !== '' &&
				this.currentSender.address_line1.trim() !== '' &&
				this.currentSender.address_city.trim() !== '' &&
				this.currentSender.address_state.trim() !== '' &&
				this.currentSender.address_zip.trim() !== ''
			)
		},
		// Switch for the send day: off clears it, on defaults to Friday
		receiverBatched: {
			get() {
				return !!this.currentReceiver.send_day
			},
			set(value) {
				this.currentReceiver.send_day = value ? this.currentReceiver.send_day || 'friday' : ''
			},
		},
		canSaveReceiver() {
			return (
				this.currentReceiver.name.trim() !== '' &&
				this.currentReceiver.address.trim() !== '' &&
				this.currentReceiver.city.trim() !== '' &&
				this.currentReceiver.state.trim() !== '' &&
				this.currentReceiver.zip.trim() !== ''
			)
		},
		canSaveEmailProvider() {
			return this.currentEmailProvider.name.trim() !== '' && this.currentEmailProvider.provider.trim() !== ''
		},
		canSaveEmailTemplate() {
			return (
				this.currentEmailTemplate.name.trim() !== '' &&
				this.currentEmailTemplate.code.trim() !== '' &&
				this.currentEmailTemplate.subject.trim() !== '' &&
				this.currentEmailTemplate.html_content.trim() !== ''
			)
		},
	},
	watch: {
		activeTab(newTab) {
			if (newTab === 'statistics') {
				// Initialize charts when statistics tab is activated
				this.$nextTick(() => {
					this.initializeCharts()
				})
			}
		},
	},
	methods: {
		setActiveTab(tab) {
			this.activeTab = tab
			if (tab === 'emailProviders') this.fetchEmailProviders()
			if (tab === 'emailTemplates') this.fetchEmailTemplates()
			if (tab === 'emailQueue') this.fetchEmailQueue()
			// Update URL query parameter without page reload
			this.$router
				.push({
					path: this.$route.path,
					query: { ...this.$route.query, tab },
				})
				.catch(() => {}) // Catch navigation duplicated error
		},
		saveSettings() {
			// TODO: Implement save functionality
			console.log('Saving settings:', this.settings)
			// Show success message or handle API call
		},
		async fetchEmailProviders() {
			this.loadingEmailProviders = true
			try {
				const response = await restApi.get('/email-provider-configs')
				this.emailProviderConfigs = response.data || []
			} catch (error) {
				console.error('Error fetching email providers:', error)
				this.emailProviderConfigs = []
			} finally {
				this.loadingEmailProviders = false
			}
		},
		openCreateEmailProviderOffcanvas() {
			this.isEditingEmailProvider = false
			this.resetEmailProviderForm()
		},
		editEmailProvider(provider) {
			this.isEditingEmailProvider = true
			this.currentEmailProvider = {
				id: provider.id,
				name: provider.name || '',
				provider: provider.provider || 'sendgrid',
				status: provider.status || 'active',
				default_from_name: provider.default_from_name || '',
				default_from_email: provider.default_from_email || '',
				default_reply_to: provider.default_reply_to || '',
				settings: {
					api_key: provider.settings?.api_key || '',
					server_token: provider.settings?.server_token || '',
					base_url: provider.settings?.base_url || '',
					message_stream: provider.settings?.message_stream || '',
					webhook_public_key: provider.settings?.webhook_public_key || '',
					webhook_secret: provider.settings?.webhook_secret || '',
				},
			}
		},
		resetEmailProviderForm() {
			this.currentEmailProvider = {
				id: null,
				name: '',
				provider: 'sendgrid',
				status: 'active',
				default_from_name: '',
				default_from_email: '',
				default_reply_to: '',
				settings: {
					api_key: '',
					server_token: '',
					base_url: '',
					message_stream: '',
					webhook_public_key: '',
					webhook_secret: '',
				},
			}
		},
		async saveEmailProvider() {
			if (!this.canSaveEmailProvider || this.savingEmailProvider) return

			this.savingEmailProvider = true
			try {
				const payload = { ...this.currentEmailProvider }
				if (!payload.settings.api_key) delete payload.settings.api_key
				if (!payload.settings.server_token) delete payload.settings.server_token
				if (!payload.settings.base_url) delete payload.settings.base_url
				if (!payload.settings.message_stream) delete payload.settings.message_stream
				if (!payload.settings.webhook_public_key) delete payload.settings.webhook_public_key
				if (!payload.settings.webhook_secret) delete payload.settings.webhook_secret

				if (this.isEditingEmailProvider && payload.id) {
					await restApi.put(`/email-provider-configs/${payload.id}`, payload)
				} else {
					await restApi.post('/email-provider-configs', payload)
				}

				this.$swal.fire({
					icon: 'success',
					title: `Email provider ${this.isEditingEmailProvider ? 'updated' : 'created'} successfully!`,
					timer: 2500,
					showConfirmButton: false,
				})

				this.$createEmailProviderOffcanvas.hide()
				await this.fetchEmailProviders()
			} catch (error) {
				console.error('Error saving email provider:', error)
				this.$swal.fire({
					icon: 'error',
					title: 'Failed to save email provider',
					text: error.response?.data?.message || error.message || 'An error occurred while saving the provider',
				})
			} finally {
				this.savingEmailProvider = false
			}
		},
		async deleteEmailProvider(id) {
			const result = await this.$swal.fire({
				title: 'Delete provider?',
				text: 'This action cannot be undone.',
				icon: 'warning',
				showCancelButton: true,
				confirmButtonText: 'Delete',
				toast: false,
				position: 'center',
				timer: null,
			})

			if (!result.isConfirmed) return

			try {
				await restApi.delete(`/email-provider-configs/${id}`)
				await this.fetchEmailProviders()
				this.$swal.fire({ icon: 'success', title: 'Email provider deleted', timer: 2000, showConfirmButton: false })
			} catch (error) {
				console.error('Error deleting email provider:', error)
				this.$swal.fire({ icon: 'error', title: 'Failed to delete email provider', text: error.response?.data?.message || error.message })
			}
		},
		async fetchEmailTemplates() {
			this.loadingEmailTemplates = true
			try {
				const response = await restApi.get('/email-templates')
				this.emailTemplates = response.data || []
			} catch (error) {
				console.error('Error fetching email templates:', error)
				this.emailTemplates = []
			} finally {
				this.loadingEmailTemplates = false
			}
		},
		openCreateEmailTemplateOffcanvas() {
			this.isEditingEmailTemplate = false
			this.resetEmailTemplateForm()
		},
		editEmailTemplate(template) {
			this.isEditingEmailTemplate = true
			this.currentEmailTemplate = {
				id: template.id,
				name: template.name || '',
				code: template.code || '',
				description: template.description || '',
				subject: template.subject || '',
				preheader: template.preheader || '',
				html_content: template.html_content || '',
				text_content: template.text_content || '',
				from_name: template.from_name || '',
				from_email: template.from_email || '',
				reply_to_email: template.reply_to_email || '',
				category: template.category || '',
				status: template.status || 'draft',
			}
		},
		resetEmailTemplateForm() {
			this.currentEmailTemplate = {
				id: null,
				name: '',
				code: '',
				description: '',
				subject: '',
				preheader: '',
				html_content: '',
				text_content: '',
				from_name: '',
				from_email: '',
				reply_to_email: '',
				category: '',
				status: 'draft',
			}
		},
		async saveEmailTemplate() {
			if (!this.canSaveEmailTemplate || this.savingEmailTemplate) return

			this.savingEmailTemplate = true
			try {
				const payload = { ...this.currentEmailTemplate }
				if (this.isEditingEmailTemplate && payload.id) {
					await restApi.put(`/email-templates/${payload.id}`, payload)
				} else {
					await restApi.post('/email-templates', payload)
				}

				this.$swal.fire({
					icon: 'success',
					title: `Email template ${this.isEditingEmailTemplate ? 'updated' : 'created'} successfully!`,
					timer: 2500,
					showConfirmButton: false,
				})

				this.$createEmailTemplateOffcanvas.hide()
				await this.fetchEmailTemplates()
			} catch (error) {
				console.error('Error saving email template:', error)
				this.$swal.fire({
					icon: 'error',
					title: 'Failed to save email template',
					text: error.response?.data?.message || error.message || 'An error occurred while saving the template',
				})
			} finally {
				this.savingEmailTemplate = false
			}
		},
		async deleteEmailTemplate(id) {
			const result = await this.$swal.fire({
				title: 'Delete template?',
				text: 'This action cannot be undone.',
				icon: 'warning',
				showCancelButton: true,
				confirmButtonText: 'Delete',
				toast: false,
				position: 'center',
				timer: null,
			})

			if (!result.isConfirmed) return

			try {
				await restApi.delete(`/email-templates/${id}`)
				await this.fetchEmailTemplates()
				this.$swal.fire({ icon: 'success', title: 'Email template deleted', timer: 2000, showConfirmButton: false })
			} catch (error) {
				console.error('Error deleting email template:', error)
				this.$swal.fire({ icon: 'error', title: 'Failed to delete email template', text: error.response?.data?.message || error.message })
			}
		},
		async fetchEmailQueue() {
			this.loadingEmailQueue = true
			try {
				const params = {}
				if (this.emailQueueFilters.campaign_source) params.campaign_source = this.emailQueueFilters.campaign_source
				if (this.emailQueueFilters.status) params.status = this.emailQueueFilters.status

				const response = await restApi.get('/campaign-email-items', { params })
				const payload = response.data || {}
				this.emailQueueItems = payload.data || []
				this.emailQueueMeta.total = payload.total || 0
			} catch (error) {
				console.error('Error fetching email queue:', error)
				this.emailQueueItems = []
				this.emailQueueMeta.total = 0
			} finally {
				this.loadingEmailQueue = false
			}
		},
		clearEmailQueueFilters() {
			this.emailQueueFilters = {
				campaign_source: '',
				status: '',
			}
			this.fetchEmailQueue()
		},
		async processScheduledEmails() {
			this.processingScheduledEmails = true
			try {
				const response = await restApi.post('/campaign-email-items/process-scheduled', { limit: 25 })
				await this.fetchEmailQueue()
				const summary = response.data?.summary || {}
				this.$swal.fire({
					icon: 'success',
					title: `Processed ${summary.processed || 0} scheduled email item(s)`,
					text: `Sent: ${summary.sent || 0} | Retrying: ${summary.retrying || 0} | Failed: ${summary.failed || 0}`,
					timer: 2500,
					showConfirmButton: false,
				})
			} catch (error) {
				console.error('Error processing scheduled emails:', error)
				this.$swal.fire({
					icon: 'error',
					title: 'Failed to process scheduled emails',
					text: error.response?.data?.message || error.message || 'An error occurred while processing the queue',
				})
			} finally {
				this.processingScheduledEmails = false
			}
		},
		getEmailQueueStatusClass(status) {
			const mapping = {
				prepared: 'date',
				scheduled: 'hook',
				queued: 'hook',
				sent: 'recurring',
				delivered: 'recurring',
				opened: 'recurring',
				clicked: 'recurring',
				bounced: 'date',
				failed: 'date',
			}
			return mapping[status] || 'date'
		},
		formatQueueDate(value) {
			if (!value) return 'N/A'
			return new Date(value).toLocaleString()
		},

		// LOB Settings Methods
		async getLobSettings() {
			try {
				const response = await restApi.get('/settings', {
					params: { name: 'LobSettings', type: 'settings' },
				})
				if (response.data) {
					this.lobSettings = JSON.parse(response.data.data)
					this.senders = this.lobSettings.senders || []
					this.receivers = this.lobSettings.receivers || []
				} else {
					this.lobSettings = {
						campaignsCheckInterval: 'daily',
						campaignsCheckIntervalString: '0 0 * * *',
						apiKey: '',
						apiKeyTest: '',
						apiVersion: '',
						apiUrl: '',
						senders: [],
						receivers: [],
					}
					this.senders = []
					this.receivers = []
				}
			} catch (error) {
				console.error('Error fetching LOB settings:', error)
				this.$swal.fire({
					icon: 'error',
					title: 'Failed to load LOB settings',
					text: error.response?.data?.message || error.message || 'An error occurred',
					timer: 5000,
				})
				this.lobSettings = {
					campaignsCheckInterval: 'daily',
					campaignsCheckIntervalString: '0 0 * * *',
					apiKey: '',
					apiKeyTest: '',
					apiVersion: '',
					apiUrl: '',
					senders: [],
					receivers: [],
				}
				this.senders = []
				this.receivers = []
			}
		},

		async saveLobSettings() {
			if (this.savingLobSettings) return

			this.savingLobSettings = true
			try {
				// Update senders and always-on receivers in lobSettings
				this.lobSettings.senders = this.senders
				this.lobSettings.receivers = this.receivers

				await restApi.post('/settings', {
					name: 'LobSettings',
					type: 'settings',
					data: JSON.stringify(this.lobSettings),
				})

				this.$swal.fire({
					icon: 'success',
					title: 'LOB Settings saved successfully!',
					timer: 3000,
					showConfirmButton: false,
				})
			} catch (error) {
				console.error('Error saving LOB settings:', error)
				this.$swal.fire({
					icon: 'error',
					title: 'Failed to save LOB settings',
					text: error.response?.data?.message || error.message || 'An error occurred',
				})
			} finally {
				this.savingLobSettings = false
			}
		},

		changeCronString(period) {
			if (this.lobSettings[period] === 'hourly') {
				this.lobSettings[period + 'String'] = '0 * * * *'
			} else if (this.lobSettings[period] === 'daily') {
				this.lobSettings[period + 'String'] = '0 0 * * *'
			} else if (this.lobSettings[period] === 'weekly') {
				this.lobSettings[period + 'String'] = '0 0 * * 0'
			} else if (this.lobSettings[period] === 'monthly') {
				this.lobSettings[period + 'String'] = '0 0 1 * *'
			}
		},

		// Sender Management Methods
		openCreateSenderOffcanvas() {
			this.isEditingSender = false
			this.editingSenderIndex = null
			this.resetSenderForm()
		},

		editSender(sender, index) {
			this.isEditingSender = true
			this.editingSenderIndex = index
			this.currentSender = { ...sender }
		},

		resetSenderForm() {
			this.currentSender = {
				name: '',
				address_line1: '',
				address_city: '',
				address_state: '',
				address_zip: '',
			}
		},

		async saveSender() {
			if (!this.canSaveSender || this.savingSender) return

			this.savingSender = true
			try {
				if (this.isEditingSender) {
					// Update existing sender
					this.senders[this.editingSenderIndex] = { ...this.currentSender }
				} else {
					// Add new sender
					this.senders.push({ ...this.currentSender })
				}

				// Save to LOB settings
				await this.saveLobSettings()

				this.$swal.fire({
					icon: 'success',
					title: `Sender ${this.isEditingSender ? 'updated' : 'added'} successfully!`,
					timer: 3000,
					showConfirmButton: false,
				})

				this.$createSenderOffcanvas.hide()
			} catch (error) {
				console.error('Error saving sender:', error)
			} finally {
				this.savingSender = false
			}
		},

		async deleteSender(index) {
			const result = await this.$swal.fire({
				title: 'Are you sure?',
				text: 'This action cannot be undone!',
				icon: 'warning',
				showCancelButton: true,
				confirmButtonColor: '#d33',
				cancelButtonColor: '#3085d6',
				confirmButtonText: 'Yes, delete it!',
				toast: false,
				position: 'center',
				showConfirmButton: true,
				timer: null,
				timerProgressBar: false,
			})

			if (result.isConfirmed) {
				try {
					this.senders.splice(index, 1)
					await this.saveLobSettings()

					this.$swal.fire({
						icon: 'success',
						title: 'Sender deleted successfully!',
						timer: 3000,
						showConfirmButton: false,
					})
				} catch (error) {
					console.error('Error deleting sender:', error)
					this.$swal.fire({
						icon: 'error',
						title: 'Failed to delete sender',
						text: 'An error occurred while deleting the sender',
					})
				}
			}
		},

		// Always-On Receiver Management Methods
		openCreateReceiverOffcanvas() {
			this.isEditingReceiver = false
			this.editingReceiverIndex = null
			this.resetReceiverForm()
		},

		editReceiver(receiver, index) {
			this.isEditingReceiver = true
			this.editingReceiverIndex = index
			// Rows saved before the flag existed count as enabled.
			this.currentReceiver = { ...receiver, enabled: receiver.enabled !== false, send_day: receiver.send_day || '' }
		},

		resetReceiverForm() {
			this.currentReceiver = {
				name: '',
				address: '',
				city: '',
				state: '',
				zip: '',
				enabled: true,
				send_day: '',
			}
		},

		// Table subtitle: when the receiver's copies are mailed
		receiverScheduleLabel(receiver) {
			return receiver.send_day ? `Mailed every ${this.$options.filters.capitalize(receiver.send_day)}` : 'Mailed with each campaign'
		},

		async saveReceiver() {
			if (!this.canSaveReceiver || this.savingReceiver) return

			this.savingReceiver = true
			try {
				if (this.isEditingReceiver) {
					this.receivers.splice(this.editingReceiverIndex, 1, { ...this.currentReceiver })
				} else {
					this.receivers.push({ ...this.currentReceiver })
				}

				// Save to LOB settings
				await this.saveLobSettings()

				this.$swal.fire({
					icon: 'success',
					title: `Receiver ${this.isEditingReceiver ? 'updated' : 'added'} successfully!`,
					timer: 3000,
					showConfirmButton: false,
				})

				this.$createReceiverOffcanvas.hide()
			} catch (error) {
				console.error('Error saving receiver:', error)
			} finally {
				this.savingReceiver = false
			}
		},

		// Flip a receiver on/off without removing its address
		async toggleReceiver(index) {
			const receiver = this.receivers[index]
			this.receivers.splice(index, 1, { ...receiver, enabled: receiver.enabled === false })

			try {
				await this.saveLobSettings()
			} catch (error) {
				console.error('Error toggling receiver:', error)
				// Put the row back the way it was so the table matches what is stored
				this.receivers.splice(index, 1, receiver)
			}
		},

		async deleteReceiver(index) {
			const result = await this.$swal.fire({
				title: 'Are you sure?',
				text: 'This receiver will stop getting a copy of new campaigns.',
				icon: 'warning',
				showCancelButton: true,
				confirmButtonColor: '#d33',
				cancelButtonColor: '#3085d6',
				confirmButtonText: 'Yes, delete it!',
				toast: false,
				position: 'center',
				showConfirmButton: true,
				timer: null,
				timerProgressBar: false,
			})

			if (result.isConfirmed) {
				try {
					this.receivers.splice(index, 1)
					await this.saveLobSettings()

					this.$swal.fire({
						icon: 'success',
						title: 'Receiver deleted successfully!',
						timer: 3000,
						showConfirmButton: false,
					})
				} catch (error) {
					console.error('Error deleting receiver:', error)
					this.$swal.fire({
						icon: 'error',
						title: 'Failed to delete receiver',
						text: 'An error occurred while deleting the receiver',
					})
				}
			}
		},

		// Statistics Methods
		async loadStatistics() {
			this.loadingStatistics = true
			try {
				// Mock data for now - replace with actual API calls
				this.statistics = {
					totalCampaigns: Math.floor(Math.random() * 500) + 100,
					activeTriggers: Math.floor(Math.random() * 50) + 10,
					totalDeliveries: Math.floor(Math.random() * 10000) + 1000,
					successRate: Math.floor(Math.random() * 20) + 80,
					avgResponseTime: Math.floor(Math.random() * 200) + 50,
					apiCallsToday: Math.floor(Math.random() * 5000) + 500,
					errorRate: (Math.random() * 2).toFixed(1),
					cacheHitRate: Math.floor(Math.random() * 20) + 75,
					queueSize: Math.floor(Math.random() * 100),
					lastSync: new Date().toLocaleString(),
				}
			} catch (error) {
				console.error('Error loading statistics:', error)
			} finally {
				this.loadingStatistics = false
			}
		},

		initializeCharts() {
			// Initialize Campaign Activity Chart
			const activityCtx = document.getElementById('campaignActivityChart')
			if (activityCtx) {
				new Chart(activityCtx, {
					type: 'line',
					data: {
						labels: Array.from({ length: 30 }, (_, i) => `Day ${i + 1}`),
						datasets: [
							{
								label: 'Campaigns Created',
								data: Array.from({ length: 30 }, () => Math.floor(Math.random() * 50) + 10),
								borderColor: '#3b82f6',
								backgroundColor: 'rgba(59, 130, 246, 0.1)',
								borderWidth: 2,
								fill: true,
							},
						],
					},
					options: {
						responsive: true,
						maintainAspectRatio: false,
						plugins: {
							legend: {
								display: false,
							},
						},
						scales: {
							y: {
								beginAtZero: true,
								grid: {
									color: '#f3f4f6',
								},
							},
							x: {
								grid: {
									display: false,
								},
							},
						},
					},
				})
			}

			// Initialize Campaign Types Chart
			const typesCtx = document.getElementById('campaignTypesChart')
			if (typesCtx) {
				new Chart(typesCtx, {
					type: 'doughnut',
					data: {
						labels: ['Postcard', 'Letter', 'Email', 'SMS'],
						datasets: [
							{
								data: [45, 30, 15, 10],
								backgroundColor: ['#3b82f6', '#10b981', '#f59e0b', '#ef4444'],
								borderWidth: 0,
							},
						],
					},
					options: {
						responsive: true,
						maintainAspectRatio: false,
						plugins: {
							legend: {
								position: 'bottom',
								labels: {
									padding: 20,
									usePointStyle: true,
								},
							},
						},
					},
				})
			}
		},

		// Trigger Management Methods
		async fetchTriggers() {
			this.loadingTriggers = true
			console.log('Fetching triggers from API...')
			try {
				const response = await restApi.get('/auto-campaign-triggers')
				console.log('API Response:', response)

				// Handle different response formats
				const triggers = response.data?.data || response.data || []
				console.log('Parsed triggers:', triggers)

				this.triggers = Array.isArray(triggers) ? triggers : []
				console.log('Final triggers array:', this.triggers)
			} catch (error) {
				console.error('Error fetching triggers:', error)
				console.error('Error response:', error.response)

				this.$swal.fire({
					icon: 'error',
					title: 'Failed to load triggers',
					text: error.response?.data?.message || error.message || 'An error occurred while loading triggers',
					timer: 5000,
				})
				this.triggers = []
			} finally {
				this.loadingTriggers = false
			}
		},

		openCreateTriggerOffcanvas() {
			this.isEditingTrigger = false
			this.resetTriggerForm()
		},

		async fetchKnownHooks() {
			try {
				const { data } = await restApi.get('/auto-campaign-hooks')
				this.knownHooks = data?.hooks || []
			} catch (error) {
				console.error('Error fetching known hooks:', error)
			}
		},
		editTrigger(trigger) {
			this.isEditingTrigger = true
			this.currentTrigger = { ...trigger, hook: trigger.hook || '' }

			// Parse conditions if they exist
			if (trigger.conditions) {
				try {
					const conditions = typeof trigger.conditions === 'string' ? JSON.parse(trigger.conditions) : trigger.conditions
					const radiusConditions = conditions.radius_conditions

					this.useRadius = !!radiusConditions
					this.radiusSize = radiusConditions?.size || ''
					this.patternDataFrom = conditions.pattern_data_from || ''

					// Load additional conditions from radius_conditions
					this.conditions = {
						sameState: radiusConditions?.sameState || false,
						sameCounty: radiusConditions?.sameCounty || false,
						states: Array.isArray(radiusConditions?.states) ? radiusConditions.states : [],
						twClient: radiusConditions?.twClient || false,
						hgClient: radiusConditions?.hgClient || false,
						notTwClient: radiusConditions?.notTwClient || false,
						notHgClient: radiusConditions?.notHgClient || false,
						clerkMinutesClient: radiusConditions?.clerkMinutesClient || false,
						notClerkMinutesClient: radiusConditions?.notClerkMinutesClient || false,
						notReceivedCampaignLast4Weeks: radiusConditions?.notReceivedCampaignLast4Weeks || false,
						population_from: radiusConditions?.population_from || null,
						population_to: radiusConditions?.population_to || null,
					}
				} catch (e) {
					console.error('Error parsing conditions:', e)
				}
			} else {
				this.useRadius = false
				this.radiusSize = ''
				this.patternDataFrom = ''
				this.resetConditions()
			}
		},

		resetTriggerForm() {
			this.currentTrigger = {
				id: null,
				name: '',
				reason: '',
				trigger_date: '',
				hook: '',
				conditions: null,
				filters: null,
				unique_url_domain: '',
			}
			this.useRadius = false
			this.radiusSize = ''
			this.patternDataFrom = ''
			this.resetConditions()
		},

		resetConditions() {
			this.conditions = {
				sameState: false,
				sameCounty: false,
				states: [],
				twClient: false,
				hgClient: false,
				notTwClient: false,
				notHgClient: false,
				clerkMinutesClient: false,
				notClerkMinutesClient: false,
				notReceivedCampaignLast4Weeks: false,
				population_from: null,
				population_to: null,
			}
		},

		selectAllStates() {
			this.conditions.states = this.usStates.map(state => state.abbreviation)
		},

		async saveTrigger() {
			if (!this.canSaveTrigger || this.savingTrigger) {
				return
			}

			// Validate population range fields
			const hasPopFrom = this.conditions.population_from !== null && this.conditions.population_from !== ''
			const hasPopTo = this.conditions.population_to !== null && this.conditions.population_to !== ''

			if (hasPopFrom !== hasPopTo) {
				this.$swal.fire({
					icon: 'error',
					title: 'Validation Error',
					text: 'Both "Population From" and "Population To" fields are required if you want to filter by population range.',
				})
				return
			}

			// Validate that population_from is less than population_to
			if (hasPopFrom && hasPopTo && this.conditions.population_from >= this.conditions.population_to) {
				this.$swal.fire({
					icon: 'error',
					title: 'Validation Error',
					text: '"Population From" must be less than "Population To".',
				})
				return
			}

			this.savingTrigger = true

			try {
				// Prepare trigger data
				const triggerData = {
					name: this.currentTrigger.name.trim(),
					reason: this.currentTrigger.reason?.trim() || null,
					hook: (this.currentTrigger.hook || '').trim() || null,
					trigger_date: null, // Only hook / manual triggers for now
					conditions: null,
					filters: null,
					unique_url_domain: this.currentTrigger.unique_url_domain?.trim() || null,
				}

				// Build conditions object
				const conditionsObject = {}

				// Add radius conditions if enabled
				if (this.useRadius && this.radiusSize) {
					const radiusConditions = {
						size: parseInt(this.radiusSize),
					}

					// Add only selected additional conditions
					if (this.conditions.sameState) radiusConditions.sameState = true
					if (this.conditions.sameCounty) radiusConditions.sameCounty = true
					if (this.conditions.states.length) radiusConditions.states = [...this.conditions.states]
					if (this.conditions.twClient) radiusConditions.twClient = true
					if (this.conditions.hgClient) radiusConditions.hgClient = true
					if (this.conditions.notTwClient) radiusConditions.notTwClient = true
					if (this.conditions.notHgClient) radiusConditions.notHgClient = true
					if (this.conditions.clerkMinutesClient) radiusConditions.clerkMinutesClient = true
					if (this.conditions.notClerkMinutesClient) radiusConditions.notClerkMinutesClient = true
					if (this.conditions.notReceivedCampaignLast4Weeks) radiusConditions.notReceivedCampaignLast4Weeks = true

					// Add population filters
					if (this.conditions.population_from !== null && this.conditions.population_to !== null) {
						radiusConditions.population_from = parseInt(this.conditions.population_from)
						radiusConditions.population_to = parseInt(this.conditions.population_to)
					}

					conditionsObject.radius_conditions = radiusConditions
				}
				
				// Add pattern data source if selected
				if (this.patternDataFrom) {
					conditionsObject.pattern_data_from = this.patternDataFrom
				}
				
				// Only set conditions if there are any
				if (Object.keys(conditionsObject).length > 0) {
					triggerData.conditions = JSON.stringify(conditionsObject)
				}

				if (this.isEditingTrigger) {
					await restApi.put(`/auto-campaign-triggers/${this.currentTrigger.id}`, triggerData)
				} else {
					await restApi.post('/auto-campaign-triggers', triggerData)
				}

				this.$swal.fire({
					icon: 'success',
					title: `Trigger ${this.isEditingTrigger ? 'updated' : 'created'} successfully!`,
					timer: 3000,
					showConfirmButton: false,
				})

				this.$createTriggerOffcanvas.hide()
				await this.fetchTriggers()
			} catch (error) {
				console.error('Error saving trigger:', error)

				let errorMessage = 'An error occurred while saving the trigger'
				if (error.response?.data) {
					if (error.response.data.message) {
						errorMessage = error.response.data.message
					} else if (error.response.data.errors) {
						// Handle Laravel validation errors
						const errors = Object.values(error.response.data.errors).flat()
						errorMessage = errors.join(', ')
					}
				}

				this.$swal.fire({
					icon: 'error',
					title: `Failed to ${this.isEditingTrigger ? 'update' : 'create'} trigger`,
					text: errorMessage,
				})
			} finally {
				this.savingTrigger = false
			}
		},

		async deleteTrigger(triggerId) {
			const result = await this.$swal.fire({
				title: 'Are you sure?',
				text: 'This action cannot be undone!',
				icon: 'warning',
				showCancelButton: true,
				confirmButtonColor: '#d33',
				cancelButtonColor: '#3085d6',
				confirmButtonText: 'Yes, delete it!',
				// Override global toast settings for this dialog
				toast: false,
				position: 'center',
				showConfirmButton: true,
				timer: null,
				timerProgressBar: false,
			})

			if (result.isConfirmed) {
				try {
					await restApi.delete(`/auto-campaign-triggers/${triggerId}`)
					this.$swal.fire({
						icon: 'success',
						title: 'Trigger deleted successfully!',
						timer: 3000,
						showConfirmButton: false,
					})
					await this.fetchTriggers()
				} catch (error) {
					console.error('Error deleting trigger:', error)

					let errorMessage = 'An error occurred while deleting the trigger'
					if (error.response?.data?.message) {
						errorMessage = error.response.data.message
					}

					this.$swal.fire({
						icon: 'error',
						title: 'Failed to delete trigger',
						text: errorMessage,
					})
				}
			}
		},

		getDayName(dayNumber) {
			const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
			return days[parseInt(dayNumber)] || 'N/A'
		},

		getInitials(name) {
			return name
				.split(' ')
				.map(word => word.charAt(0))
				.join('')
				.substring(0, 2)
				.toUpperCase()
		},

		formatTriggerDate(dateString) {
			if (!dateString) return 'N/A'
			const date = new Date(dateString)
			return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
		},
	},
}
</script>

<style scoped>
/* Header Styles */
.page-header {
	padding: 0;
}

.back-btn {
	width: 40px;
	height: 40px;
	display: flex;
	align-items: center;
	justify-content: center;
	background: #ffffff;
	border: 1px solid #e0e1e1;
	border-radius: 50%;
	color: #64748b;
	text-decoration: none;
	transition: all 0.2s ease;
	flex-shrink: 0;
}

.back-btn:hover {
	background: #f8f9fa;
	border-color: #d1d5db;
	color: #475569;
	text-decoration: none;
}

.page-title {
	font-size: 20px;
	font-weight: 600;
	color: #111827;
	margin: 0 0 4px 0;
	line-height: 1.2;
}

.page-subtitle {
	font-size: 14px;
	color: #6b7280;
	margin: 0;
	line-height: 1.4;
}

/* Tab Styles - Custom Nav Pills */
.custom-nav-pills {
	background-color: #f3f4f6;
	border-radius: 25px;
	padding: 4px;
	margin-bottom: 24px;
	width: fit-content;
	gap: 0;
}

.custom-nav-pills .nav-item {
	margin: 0;
}

.custom-nav-pills .nav-link {
	background: none;
	border: none;
	padding: 8px 16px;
	font-size: 14px;
	font-weight: 500;
	color: #6b7280;
	cursor: pointer;
	border-radius: 20px;
	transition: all 0.2s ease;
	display: flex;
	align-items: center;
	gap: 8px;
	white-space: nowrap;
	margin: 0;
}

.custom-nav-pills .nav-link.active {
	background-color: #ffffff !important;
	color: #111827 !important;
	box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
}

.custom-nav-pills .nav-link:hover:not(.active) {
	color: #374151;
	background-color: transparent;
}

.tab-text {
	font-weight: 500;
}

.tab-badge {
	background-color: #e5e7eb;
	color: #6b7280;
	padding: 2px 8px;
	border-radius: 12px;
	font-size: 12px;
	font-weight: 600;
	min-width: 20px;
	text-align: center;
}

.custom-nav-pills .nav-link.active .tab-badge {
	background-color: #dbeafe;
	color: #1e40af;
}

/* Content Styles */
.tab-content {
	background: white;
	border-radius: 20px;
	margin: 5px;
}

.settings-section {
	margin-bottom: 2rem;
}

/* Main Tab Styles */
.section-header-main {
	padding: 24px 24px 16px 24px;
	border-bottom: 1px solid #f3f4f6;
	background-color: #f9fafb;
}

.section-title-main {
	font-size: 18px;
	font-weight: 600;
	color: #111827;
	margin: 0 0 8px 0;
	display: flex;
	align-items: center;
}

.section-description-main {
	font-size: 14px;
	color: #6b7280;
	margin: 0;
}

.section-content-main {
	padding: 24px;
}

.section-title {
	font-size: 1.5rem;
	font-weight: 600;
	color: #1f2937;
	margin-bottom: 0.5rem;
}

.section-description {
	color: #6b7280;
	margin-bottom: 2rem;
}

/* Form Styles */
.form-group {
	margin-bottom: 1.5rem;
}

.form-label {
	font-weight: 600;
	color: #374151;
	margin-bottom: 0.5rem;
}

.form-control {
	border: 1px solid #d1d5db;
	border-radius: 0.5rem;
	padding: 0.75rem 1rem;
	font-size: 0.875rem;
}

.form-control:focus {
	border-color: #3b82f6;
	box-shadow: 0 0 0 0.2rem rgba(59, 130, 246, 0.1);
}

.form-text {
	color: #6b7280;
	font-size: 0.875rem;
	margin-top: 0.5rem;
}

.input-group-text {
	background-color: #f9fafb;
	border: 1px solid #d1d5db;
	color: #6b7280;
}

/* Sidebar Styles */
.settings-sidebar {
	padding-left: 2rem;
}

.logo-upload-section {
	background: #f9fafb;
	border: 1px solid #e5e7eb;
	border-radius: 0.75rem;
	padding: 1.5rem;
}

.logo-placeholder {
	width: 80px;
	height: 80px;
	background: #fbbf24;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	margin: 1rem 0;
}

.logo-initials {
	font-size: 1.5rem;
	font-weight: 700;
	color: white;
}

.upload-section h6 {
	font-weight: 600;
	color: #374151;
	margin-bottom: 0.5rem;
}

/* Footer Styles */
.settings-footer {
	border-top: 1px solid #e5e7eb;
	padding-top: 2rem;
}

/* Save Button Styles */
.save-btn {
	display: inline-flex;
	align-items: center;
	padding: 10px 20px;
	background: #ffffff;
	color: #64748b;
	border: 1px solid #e0e1e1;
	border-radius: 25px;
	text-decoration: none;
	font-size: 14px;
	font-weight: 500;
	transition: all 0.2s ease;
	white-space: nowrap;
	cursor: pointer;
}

.save-btn:hover {
	background: #f8f9fa;
	border-color: #d1d5db;
	color: #475569;
	text-decoration: none;
}

.save-btn .save-icon {
	transition: transform 0.3s ease;
}

.save-btn:hover .save-icon {
	transform: rotate(10deg);
}

.text-primary {
	color: #3b82f6 !important;
	text-decoration: none;
}

.text-primary:hover {
	text-decoration: underline;
}

/* Empty State Styles */
.empty-state {
	text-align: center;
	padding: 4rem 2rem;
	color: #6b7280;
	border-top: 15px solid #f3f4f6;
}

.empty-title {
	font-size: 1.5rem;
	font-weight: 600;
	color: #374151;
	margin-bottom: 0.5rem;
}

.empty-description {
	font-size: 1rem;
	color: #6b7280;
	margin: 0 0 1.5rem 0;
}

.empty-state-btn {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	height: 40px;
	padding: 0 20px;
	background: #ffffff;
	border: 1px solid #e0e1e1;
	border-radius: 25px;
	color: #64748b;
	text-decoration: none;
	font-size: 14px;
	font-weight: 500;
	transition: all 0.2s ease;
	cursor: pointer;
}

.empty-state-btn:hover {
	background: #f8f9fa;
	border-color: #d1d5db;
	color: #475569;
	text-decoration: none;
}

.empty-state-btn .btn-icon {
	transition: transform 0.3s ease;
}

.empty-state-btn:hover .btn-icon {
	transform: rotate(180deg);
}

/* Triggers Table Styles */
.triggers-table {
	border-top: 1px solid #f3f4f6;
}

.table-header {
	display: grid;
	grid-template-columns: 2fr 120px 180px 200px 80px;
	gap: 12px;
	padding: 12px 24px;
	background-color: #f9fafb;
	border-bottom: 1px solid #f3f4f6;
}

/* Senders Table Grid */
.senders .table-header {
	grid-template-columns: 2fr 180px 120px 80px 100px 80px;
}

.senders .table-row {
	grid-template-columns: 2fr 180px 120px 80px 100px 80px;
}

/* Receivers Table Grid */
.receivers .table-header {
	grid-template-columns: 2fr 180px 120px 80px 100px 110px 80px;
}

.receivers .table-row {
	grid-template-columns: 2fr 180px 120px 80px 100px 110px 80px;
}

.status-toggle-btn {
	background: none;
	border: none;
	padding: 0;
	cursor: pointer;
}

.header-cell {
	font-size: 12px;
	font-weight: 600;
	color: #6b7280;
	text-transform: uppercase;
	letter-spacing: 0.05em;
}

.table-row {
	display: grid;
	grid-template-columns: 2fr 120px 180px 200px 80px;
	gap: 12px;
	align-items: center;
	padding: 16px 24px;
	border-bottom: 1px solid #f9fafb;
	transition: background-color 0.2s ease;
}

.table-row:hover {
	background-color: #f9fafb;
}

.table-row:last-child {
	border-bottom: none;
}

.table-cell {
	font-size: 14px;
	color: #111827;
}

/* Trigger Info */
.trigger-info {
	display: flex;
	align-items: center;
	gap: 12px;
}

.trigger-avatar {
	width: 40px;
	height: 40px;
	border-radius: 50%;
	background-color: #e5e7eb;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 14px;
	font-weight: 600;
	color: #374151;
	flex-shrink: 0;
}

.trigger-details {
	min-width: 0;
}

.trigger-name {
	font-size: 14px;
	font-weight: 500;
	color: #111827;
	line-height: 1.4;
}

.trigger-description {
	font-size: 12px;
	color: #6b7280;
	line-height: 1.4;
}

/* Type Badge */
.type-badge {
	padding: 4px 8px;
	border-radius: 12px;
	font-size: 11px;
	font-weight: 500;
	text-transform: uppercase;
	letter-spacing: 0.05em;
}

.type-badge.date {
	background-color: #dbeafe;
	color: #1e40af;
}

.type-badge.recurring {
	background-color: #dcfce7;
	color: #166534;
}

.type-badge.hook {
	background-color: #fef3c7;
	color: #92400e;
}

/* Schedule Info */
.schedule-info {
	display: flex;
	flex-direction: column;
	gap: 2px;
}

.schedule-type {
	font-size: 14px;
	font-weight: 500;
	color: #111827;
}

/* URL Cell */
.url-cell {
	overflow: hidden;
}

.hook-url {
	font-size: 12px;
	color: #6b7280;
	font-family: monospace;
	word-break: break-all;
	overflow-wrap: break-word;
	line-height: 1.3;
	display: -webkit-box;
	-webkit-line-clamp: 2;
	line-clamp: 2;
	-webkit-box-orient: vertical;
	overflow: hidden;
}

.no-url {
	font-size: 12px;
	color: #9ca3af;
}

/* Actions */
.actions-cell {
	display: flex;
	gap: 4px;
	align-items: center;
	justify-content: flex-end;
}

.action-icon-btn {
	background: none;
	border: none;
	padding: 8px;
	border-radius: 4px;
	color: #9ca3af;
	cursor: pointer;
	transition: all 0.2s ease;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 14px;
}

.action-icon-btn:hover {
	background-color: #f3f4f6;
	color: #6b7280;
}

.action-icon-btn.delete-btn:hover {
	background-color: #fef2f2;
	color: #dc2626;
}

/* Add Trigger Button */
.add-trigger-btn {
	width: 40px;
	height: 40px;
	display: flex;
	align-items: center;
	justify-content: center;
	background: #ffffff;
	border: 1px solid #e0e1e1;
	border-radius: 50%;
	color: #64748b;
	text-decoration: none;
	transition: all 0.2s ease;
	flex-shrink: 0;
	position: relative;
	cursor: pointer;
}

.add-trigger-btn:hover {
	background: #f8f9fa;
	border-color: #d1d5db;
	color: #475569;
	text-decoration: none;
}

.add-trigger-btn .btn-icon {
	transition: transform 0.3s ease;
}

.add-trigger-btn:hover .btn-icon {
	transform: rotate(180deg);
}

.add-trigger-btn .btn-tooltip {
	position: absolute;
	right: 50px;
	top: 50%;
	transform: translateY(-50%);
	background: #374151;
	color: #ffffff;
	padding: 6px 12px;
	border-radius: 6px;
	font-size: 12px;
	font-weight: 500;
	white-space: nowrap;
	opacity: 0;
	visibility: hidden;
	transition: all 0.2s ease;
	pointer-events: none;
	z-index: 1000;
}

.add-trigger-btn .btn-tooltip::before {
	content: '';
	position: absolute;
	right: -5px;
	top: 50%;
	transform: translateY(-50%);
	border: 5px solid transparent;
	border-left-color: #374151;
}

.add-trigger-btn:hover .btn-tooltip {
	opacity: 1;
	visibility: visible;
}

/* Offcanvas Sections */
.trigger-section {
	border-bottom: 1px solid #f3f4f6;
}

.trigger-section:last-of-type {
	border-bottom: none;
}

.section-header {
	padding: 20px 24px 16px 24px;
	background-color: #f9fafb;
	border-bottom: 1px solid #f3f4f6;
}

.section-title {
	font-size: 16px;
	font-weight: 600;
	color: #111827;
	margin: 0 0 4px 0;
}

.section-description {
	font-size: 14px;
	color: #6b7280;
	margin: 0;
}

.section-content {
	padding: 20px 24px;
}

.offcanvas-footer {
	padding: 16px 24px;
	border-top: 1px solid #f3f4f6;
	background-color: #ffffff;
	display: flex;
	gap: 12px;
	justify-content: flex-end;
	margin-top: auto;
}

.offcanvas-body {
	display: flex;
	flex-direction: column;
	height: 100%;
}

/* Form styling in offcanvas */
.trigger-section .form-label {
	font-weight: 600;
	color: #374151;
	margin-bottom: 6px;
	font-size: 14px;
}

.trigger-section .form-control {
	border: 1px solid #d1d5db;
	border-radius: 6px;
	padding: 10px 12px;
	font-size: 14px;
	transition: border-color 0.2s ease;
}

.trigger-section .form-control:focus {
	border-color: #3b82f6;
	box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.trigger-section .form-text {
	color: #6b7280;
	font-size: 12px;
	margin-top: 4px;
}

.trigger-section .form-check {
	display: flex;
	align-items: center;
	gap: 8px;
}

.trigger-section .form-check-input {
	margin: 0;
}

/* Switches next to a long label must keep their size instead of shrinking */
.trigger-section .form-switch {
	padding-left: 0;
}

/* Classic pill toggle: the global theme's knob overhangs a thin track, which
   looks off at this size, so the knob sits inside the track here */
.trigger-section .form-switch .form-check-input {
	flex-shrink: 0;
	width: 2.5em;
	height: 1.4em;
	border-radius: 1em;
	background-color: #ced4da;
	cursor: pointer;
	transition: background-color 0.15s ease-in-out;
}

.trigger-section .form-switch .form-check-input::before {
	top: 0.2em;
	left: 0.2em;
	width: 1em;
	height: 1em;
	background-color: #fff;
	box-shadow: 0 1px 2px rgba(0, 0, 0, 0.25);
}

.trigger-section .form-switch .form-check-input::after {
	display: none;
}

.trigger-section .form-switch .form-check-input:checked {
	background-color: #2196f3;
}

.trigger-section .form-switch .form-check-input:checked::before {
	left: calc(100% - 1.2em);
	background-color: #fff;
}

.trigger-section .form-check-label {
	font-weight: 500;
	color: #374151;
	margin: 0;
	cursor: pointer;
}

/* Statistics Tab Styles */
.statistics-content {
	padding: 24px;
}

.stats-grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
	gap: 20px;
}

.stat-card {
	background: white;
	border-radius: 16px;
	padding: 24px;
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
	border: 1px solid #f3f4f6;
	display: flex;
	align-items: center;
	gap: 16px;
	transition: all 0.2s ease;
}

.stat-card:hover {
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
	transform: translateY(-2px);
}

.stat-icon {
	width: 48px;
	height: 48px;
	border-radius: 12px;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 20px;
	color: white;
}

.stat-icon.campaigns {
	background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.stat-icon.triggers {
	background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
}

.stat-icon.deliveries {
	background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
}

.stat-icon.success-rate {
	background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%);
}

.stat-content {
	flex: 1;
}

.stat-number {
	font-size: 28px;
	font-weight: 700;
	color: #111827;
	margin: 0 0 4px 0;
	line-height: 1;
}

.stat-label {
	font-size: 14px;
	color: #6b7280;
	margin: 0 0 8px 0;
	font-weight: 500;
}

.stat-change {
	font-size: 12px;
	font-weight: 600;
	display: flex;
	align-items: center;
	gap: 4px;
}

.stat-change.positive {
	color: #059669;
}

.stat-change.negative {
	color: #dc2626;
}

.stat-change.neutral {
	color: #6b7280;
}

.chart-card {
	background: white;
	border-radius: 16px;
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
	border: 1px solid #f3f4f6;
	overflow: hidden;
}

.chart-header {
	padding: 20px 24px 16px 24px;
	border-bottom: 1px solid #f3f4f6;
	background: #f9fafb;
}

.chart-title {
	font-size: 16px;
	font-weight: 600;
	color: #111827;
	margin: 0 0 4px 0;
}

.chart-subtitle {
	font-size: 14px;
	color: #6b7280;
	margin: 0;
}

.chart-container {
	padding: 20px;
	height: 300px;
	position: relative;
}

.dev-info-card {
	background: white;
	border-radius: 16px;
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
	border: 1px solid #f3f4f6;
	overflow: hidden;
}

.dev-header {
	padding: 20px 24px 16px 24px;
	border-bottom: 1px solid #f3f4f6;
	background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.dev-title {
	font-size: 16px;
	font-weight: 600;
	color: white;
	margin: 0;
	display: flex;
	align-items: center;
}

.dev-metrics {
	padding: 24px;
}

.metric-row {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 12px 0;
	border-bottom: 1px solid #f3f4f6;
}

.metric-row:last-child {
	border-bottom: none;
}

.metric-label {
	font-size: 14px;
	color: #6b7280;
	font-weight: 500;
}

.metric-value {
	font-size: 14px;
	color: #111827;
	font-weight: 600;
}

.metric-value.status {
	display: flex;
	align-items: center;
	gap: 6px;
}

.metric-value.status.online {
	color: #059669;
}

.metric-value.status.offline {
	color: #dc2626;
}

.metric-value.status i {
	font-size: 8px;
}

/* Additional Filters Styles */
.additional-filters-header {
	margin-bottom: 20px;
}

.additional-filters-title {
	font-size: 16px;
	font-weight: 600;
	color: #111827;
	margin-bottom: 4px;
}

.additional-filters-description {
	font-size: 14px;
	color: #6b7280;
	margin-bottom: 0;
}

.additional-filters-container {
	background: #f9fafb;
	border: 1px solid #f3f4f6;
	border-radius: 12px;
	padding: 20px;
}

.filter-group {
	margin-bottom: 24px;
}

.filter-group:last-child {
	margin-bottom: 0;
}

.filter-group-title {
	font-size: 14px;
	font-weight: 600;
	color: #374151;
	margin-bottom: 12px;
	padding-bottom: 8px;
	border-bottom: 2px solid #e5e7eb;
	display: flex;
	align-items: center;
	gap: 8px;
}

.filter-group-title:before {
	content: '';
	width: 3px;
	height: 16px;
	background: linear-gradient(135deg, #3b82f6, #1d4ed8);
	border-radius: 2px;
}

.filter-options {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
	gap: 12px;
}

.filter-option {
	display: flex;
	align-items: center;
	padding: 12px 16px;
	background: #ffffff;
	border: 1px solid #e5e7eb;
	border-radius: 8px;
	transition: all 0.2s ease;
	cursor: pointer;
	position: relative;
}

.filter-option:hover {
	border-color: #3b82f6;
	box-shadow: 0 2px 4px rgba(59, 130, 246, 0.1);
	transform: translateY(-1px);
}

.filter-option input[type="checkbox"] {
	margin-right: 12px;
	scale: 1.1;
}

.filter-option input[type="checkbox"]:checked + .filter-label {
	color: #1d4ed8;
	font-weight: 500;
}

.filter-option input[type="checkbox"]:checked + .filter-label .filter-icon {
	color: #3b82f6 !important;
}

.filter-label {
	font-size: 14px;
	color: #374151;
	cursor: pointer;
	display: flex;
	align-items: center;
	gap: 8px;
	margin: 0;
	font-weight: 500;
	transition: all 0.2s ease;
}

.filter-icon {
	font-size: 16px;
	color: #9ca3af;
	transition: all 0.2s ease;
	width: 16px;
	text-align: center;
}

.state-filter-grid {
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
	gap: 8px;
	max-height: 280px;
	overflow-y: auto;
	padding: 12px;
	background: #ffffff;
	border: 1px solid #e5e7eb;
	border-radius: 8px;
}

.state-filter-option {
	display: flex;
	align-items: center;
	gap: 8px;
	font-size: 13px;
	color: #374151;
	cursor: pointer;
}

.state-filter-option .form-check-input {
	margin: 0;
}

/* Population Range Inputs */
.population-range-inputs {
	padding: 12px 0;
}

.population-range-inputs .form-label-small {
	font-size: 13px;
	font-weight: 500;
	color: #6b7280;
	margin-bottom: 6px;
}

.population-range-inputs .form-control-sm {
	font-size: 14px;
	padding: 8px 12px;
	border-radius: 6px;
	border: 1px solid #d1d5db;
	transition: all 0.2s ease;
}

.population-range-inputs .form-control-sm:focus {
	border-color: #3b82f6;
	box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
	outline: none;
}

.population-range-inputs .form-text {
	font-size: 12px;
	color: #6b7280;
	display: flex;
	align-items: center;
	gap: 4px;
}

/* Responsive design */
@media (max-width: 768px) {
	.filter-options {
		grid-template-columns: 1fr;
	}
	
	.additional-filters-container {
		padding: 16px;
	}
}

/* Animation for when filters appear */
.additional-filters-container {
	animation: fadeInUp 0.3s ease-out;
}

@keyframes fadeInUp {
	from {
		opacity: 0;
		transform: translateY(10px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}
</style>
